import React, { useState, useEffect, useCallback, useRef } from "react";
import * as api from "./api";
import "./App.css";

// ── Constants ──────────────────────────────────────────────────────────────────
const STAGES = ["Prospecting","Qualified","Proposal","Negotiation","Closed Won","Closed Lost"];
const STAGE_COLORS = { Prospecting:"#818cf8", Qualified:"#60a5fa", Proposal:"#fbbf24", Negotiation:"#fb923c", "Closed Won":"#4ade80", "Closed Lost":"#f87171" };
const PRIORITIES = ["Low","Medium","High","Urgent"];
const PRIORITY_COLORS = { Low:"#4ade80", Medium:"#fbbf24", High:"#fb923c", Urgent:"#f87171" };

const fmt = n => new Intl.NumberFormat("en-CA",{style:"currency",currency:"CAD",maximumFractionDigits:0}).format(n||0);
const fmtDate = d => d ? new Date(d+"T12:00:00").toLocaleDateString("en-CA",{month:"short",day:"numeric",year:"numeric"}) : "—";
const isOverdue = d => d && new Date(d+"T23:59:59") < new Date();

const NAV = [
  {id:"dashboard",ico:"◈",lbl:"Dashboard"},
  {id:"contacts",ico:"◉",lbl:"Clients"},
  {id:"archived",ico:"◎",lbl:"Archived"},
  {id:"map",ico:"◐",lbl:"Map"},
  {id:"revenue",ico:"◆",lbl:"Revenue"},
  {id:"calculator",ico:"◇",lbl:"Calculator"},
  {id:"taxes",ico:"◊",lbl:"Taxes"},
  {id:"calendrier",ico:"▦",lbl:"Calendrier"},
  {id:"pipeline",ico:"◧",lbl:"Pipeline"},
  {id:"tasks",ico:"◻",lbl:"Tasks"},
];

// ── Main App ───────────────────────────────────────────────────────────────────
export default function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (api.isAuthenticated()) {
      api.auth.me()
        .then(data => setUser(data.user))
        .catch(() => api.setToken(null))
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  const handleLogin = (token, userData) => {
    api.setToken(token);
    setUser(userData);
  };

  const handleLogout = () => {
    api.setToken(null);
    setUser(null);
  };

  if (loading) return <div className="loader">Loading...</div>;
  if (!user) return <LoginScreen onLogin={handleLogin} />;
  return <CRMApp user={user} onLogout={handleLogout} />;
}

// ── Login Screen ───────────────────────────────────────────────────────────────
function LoginScreen({ onLogin }) {
  const [isRegister, setIsRegister] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [fullName, setFullName] = useState("");
  const [totpCode, setTotpCode] = useState("");
  const [needs2FA, setNeeds2FA] = useState(false);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      if (isRegister) {
        const data = await api.auth.register(email, password, fullName);
        onLogin(data.token, data.user);
      } else {
        const data = await api.auth.login(email, password, totpCode || undefined);
        if (data.requires2FA) {
          setNeeds2FA(true);
          setSubmitting(false);
          return;
        }
        onLogin(data.token, data.user);
      }
    } catch (err) {
      setError(err.message);
    }
    setSubmitting(false);
  };

  return (
    <div className="login-wrapper">
      <div className="login-box">
        <h1 className="login-title">LAVAGES<span> LANGEVIN</span></h1>
        <p className="login-sub">Gestion de clients</p>
        <form onSubmit={handleSubmit}>
          {isRegister && (
            <div className="ff">
              <label className="fl">Full Name</label>
              <input className="fi" value={fullName} onChange={e=>setFullName(e.target.value)} placeholder="Your full name" required />
            </div>
          )}
          <div className="ff">
            <label className="fl">Email</label>
            <input className="fi" type="email" value={email} onChange={e=>setEmail(e.target.value)} placeholder="you@company.com" required autoComplete="email" name="email" />
          </div>
          <div className="ff">
            <label className="fl">Password</label>
            <input className="fi" type="password" value={password} onChange={e=>setPassword(e.target.value)} placeholder={isRegister?"Min 10 characters":"Your password"} required minLength={isRegister?10:1} autoComplete={isRegister?"new-password":"current-password"} name="password" />
          </div>
          {needs2FA && (
            <div className="ff">
              <label className="fl">2FA Code</label>
              <input className="fi" value={totpCode} onChange={e=>setTotpCode(e.target.value)} placeholder="6-digit code" maxLength={6} required />
            </div>
          )}
          {error && <p className="login-err">{error}</p>}
          <button className="btn bp login-btn" type="submit" disabled={submitting}>
            {submitting ? "..." : isRegister ? "Create Account" : "Sign In"}
          </button>
        </form>
        <p className="login-toggle" onClick={()=>{setIsRegister(!isRegister);setError("");setNeeds2FA(false);}}>
          {isRegister ? "Already have an account? Sign in" : "Don't have an account? Register"}
        </p>
      </div>
    </div>
  );
}

// ── CRM App ────────────────────────────────────────────────────────────────────
function CRMApp({ user, onLogout }) {
  const [view, setView] = useState("dashboard");
  const [contacts, setContacts] = useState([]);
  const [dealsList, setDealsList] = useState([]);
  const [tasksList, setTasksList] = useState([]);
  const [scheduleJobs, setScheduleJobs] = useState([]);
  const [modal, setModal] = useState(null);
  const [search, setSearch] = useState("");
  const [notif, setNotif] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [showPwModal, setShowPwModal] = useState(false);
  const [showRateModal, setShowRateModal] = useState(false);
  const [navOrder, setNavOrder] = useState([]);
  const [editingNav, setEditingNav] = useState(false);

  // Load saved nav order on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem("ll_nav_order");
      if (stored) setNavOrder(JSON.parse(stored));
    } catch {}
  }, []);

  // Build ordered NAV: saved order first (filtered to existing ids), then any NAV
  // items not in saved order appended at the end (so future-added tabs still show).
  const orderedNav = (() => {
    if (!navOrder.length) return NAV;
    const byId = Object.fromEntries(NAV.map(n => [n.id, n]));
    const fromSaved = navOrder.filter(id => byId[id]).map(id => byId[id]);
    const savedIds = new Set(navOrder);
    const newOnes = NAV.filter(n => !savedIds.has(n.id));
    return [...fromSaved, ...newOnes];
  })();

  const moveNavItem = (idx, dir) => {
    const ids = orderedNav.map(n => n.id);
    const newIdx = idx + dir;
    if (newIdx < 0 || newIdx >= ids.length) return;
    [ids[idx], ids[newIdx]] = [ids[newIdx], ids[idx]];
    setNavOrder(ids);
    try { localStorage.setItem("ll_nav_order", JSON.stringify(ids)); } catch {}
  };

  const resetNavOrder = () => {
    setNavOrder([]);
    try { localStorage.removeItem("ll_nav_order"); } catch {}
  };

  const notify = (msg, type="ok") => { setNotif({msg,type}); setTimeout(()=>setNotif(null),3400); };

  // Load data
  const loadData = useCallback(async () => {
    try {
      const [c, d, t, sch] = await Promise.all([
        api.contacts.list(), api.deals.list(), api.tasks.list(), api.schedule.list()
      ]);
      setContacts(c.contacts || []);
      setDealsList(d.deals || []);
      setTasksList(t.tasks || []);
      setScheduleJobs(sch.jobs || []);
    } catch (err) {
      notify("Failed to load data", "err");
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  // CRUD handlers
  const handleSave = async (data) => {
    try {
      if (modal.type === "contact") {
        if (modal.data) await api.contacts.update(data.id, data);
        else await api.contacts.create(data);
        notify(modal.data ? "Contact updated" : "Contact added");
      } else if (modal.type === "deal") {
        if (modal.data) await api.deals.update(data.id, data);
        else await api.deals.create(data);
        notify(modal.data ? "Deal updated" : "Deal added");
      } else if (modal.type === "task") {
        if (modal.data) await api.tasks.update(data.id, data);
        else await api.tasks.create(data);
        notify(modal.data ? "Task updated" : "Task added");
      }
      setModal(null);
      loadData();
    } catch (err) {
      notify(err.message, "err");
    }
  };

  const deleteContact = async (id) => { await api.contacts.delete(id); notify("Contact deleted"); loadData(); };
  const archiveContact = async (id, newStatus) => {
    const status = newStatus || "Archived";
    await api.contacts.update(id, {status});
    notify(status==="Archived"?"Client archived":"Client restored");
    loadData();
  };
  const colorTagContact = async (id, tag) => {
    const contact = contacts.find(c=>c.id===id);
    if(!contact) return;
    const existing = (contact.tags||[]).filter(t=>!["booked","waiting","quote","associate"].includes(t));
    const newTags = tag ? [...existing, tag] : existing;
    await api.contacts.update(id, {tags: newTags});
    loadData();
  };
  const resetAllColors = async () => {
    const tagged = contacts.filter(c=>(c.tags||[]).some(t=>["booked","waiting","quote","associate"].includes(t)));
    for(const c of tagged) {
      const cleaned = (c.tags||[]).filter(t=>!["booked","waiting","quote","associate"].includes(t));
      await api.contacts.update(c.id, {tags: cleaned});
    }
    notify(`${tagged.length} couleurs réinitialisées`);
    loadData();
  };
  const bulkColorTag = async (ids, tag) => {
    for(const id of ids) {
      const contact = contacts.find(c=>c.id===id);
      if(!contact) continue;
      const existing = (contact.tags||[]).filter(t=>!["booked","waiting","quote","associate"].includes(t));
      const newTags = tag ? [...existing, tag] : existing;
      await api.contacts.update(id, {tags: newTags});
    }
    notify(`${ids.length} client${ids.length>1?"s":""} mis à jour`);
    loadData();
  };
  const deleteDeal = async (id) => { await api.deals.delete(id); notify("Deal deleted"); loadData(); };
  const deleteTask = async (id) => { await api.tasks.delete(id); notify("Task deleted"); loadData(); };
  const moveDeal = async (id, stage) => { await api.deals.moveStage(id, stage); loadData(); };
  const toggleTask = async (id) => { await api.tasks.toggle(id); loadData(); };
  const reloadSchedule = useCallback(async () => {
    try { const sch = await api.schedule.list(); setScheduleJobs(sch.jobs || []); }
    catch { notify("Failed to load schedule", "err"); }
  }, []);
  const createJob = async (data) => { await api.schedule.create(data); await reloadSchedule(); notify("Job ajouté"); };
  const updateJob = async (id, data) => { await api.schedule.update(id, data); await reloadSchedule(); notify("Job mis à jour"); };
  const moveJob = async (id, data) => { await api.schedule.move(id, data); await reloadSchedule(); };
  const deleteJob = async (id) => { await api.schedule.delete(id); await reloadSchedule(); notify("Job supprimé"); };

  // Metrics
  const pipeVal = dealsList.filter(d=>!["Closed Won","Closed Lost"].includes(d.stage)).reduce((s,d)=>s+d.value,0);
  const wonVal = dealsList.filter(d=>d.stage==="Closed Won").reduce((s,d)=>s+d.value,0);
  const pending = tasksList.filter(t=>!t.completed).length;
  const overdue = tasksList.filter(t=>!t.completed&&isOverdue(t.dueDate)).length;
  const cName = id => { const c = contacts.find(c=>c.id===id); return c?.fullName||"—"; };

  const searchFilter = c => {
    if(!search) return true;
    const s = search.toLowerCase();
    return (c.fullName?.toLowerCase().includes(s)||c.company?.toLowerCase().includes(s)||c.phone?.toLowerCase().includes(s)||c.email?.toLowerCase().includes(s)||c.notes?.toLowerCase().includes(s));
  };
  const filtC = contacts.filter(c=>c.status!=="Archived").filter(searchFilter);
  const filtA = contacts.filter(c=>c.status==="Archived").filter(searchFilter);
  const filtT = tasksList.filter(t=>!search||t.title.toLowerCase().includes(search.toLowerCase()));

  // Export to CSV/Excel
  const exportContacts = (list, filename) => {
    const p = (notes) => {
      if(!notes) return {prix:"",prixOut:"",prixAutre:"",rest:""};
      let prix="",prixOut="",prixAutre="",rest=[];
      (notes.split("|")).forEach(s=>{
        const t=s.trim();
        const mp=t.match(/^Prix:\s*\$?([\d,]+)/); if(mp){prix=mp[1];return;}
        const mo=t.match(/^Prix OUT:\s*\$?([\d,]+)/); if(mo){prixOut=mo[1];return;}
        const ma=t.match(/^Autre:\s*(.*)/); if(ma){prixAutre=ma[1].trim();return;}
        if(t) rest.push(t);
      });
      return {prix,prixOut,prixAutre,rest:rest.join(" | ")};
    };
    const headers = ["Nom","Adresse","Téléphone","Email","Prix","Prix OUT","Prix Autre","Durée","Status","Notes"];
    const rows = list.map(c=>{
      const n = p(c.notes);
      return [c.fullName,c.company,c.phone,c.email,n.prix,n.prixOut,n.prixAutre,n.duree||"",c.status,n.rest].map(v=>`"${(v||"").replace(/"/g,'""')}"`);
    });
    const csv = "\uFEFF" + [headers.join(","), ...rows.map(r=>r.join(","))].join("\n");
    const blob = new Blob([csv],{type:"text/csv;charset=utf-8;"});
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href=url; a.download=filename; a.click();
    URL.revokeObjectURL(url);
    notify("Export téléchargé!");
  };

  return (
    <div className="root">
      <div className={`mob-overlay${menuOpen?" open":""}`} onClick={()=>setMenuOpen(false)}/>
      <aside className={`sb${menuOpen?" open":""}`}>
        <div className="sb-logo">
          <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:3}}>
            <svg width="22" height="26" viewBox="0 0 22 26" fill="none">
              <rect x="1" y="1" width="20" height="24" rx="2" stroke="#e8b817" strokeWidth="2" fill="none"/>
              <line x1="11" y1="1" x2="11" y2="25" stroke="#e8b817" strokeWidth="1.5"/>
              <line x1="1" y1="13" x2="21" y2="13" stroke="#e8b817" strokeWidth="1.5"/>
            </svg>
            <h1>LAVAGES<span> LANGEVIN</span></h1>
          </div>
          <p>Gestion de clients</p>
        </div>
        <nav className="sb-nav">
          {orderedNav.map((n, idx) => (
            <div
              key={n.id}
              className={`ni${view===n.id && !editingNav?" on":""}`}
              onClick={editingNav ? undefined : ()=>{setView(n.id);setSearch("");setMenuOpen(false);}}
              style={editingNav ? {cursor:"default",display:"flex",alignItems:"center"} : undefined}
            >
              <span className="ni-ico">{n.ico}</span>
              <span style={{flex:1}}>{n.lbl}</span>
              {editingNav && (
                <span style={{display:"flex",gap:4,marginLeft:"auto"}}>
                  <button
                    type="button"
                    className="btn bg bsm"
                    style={{padding:"2px 8px",minWidth:0,fontSize:13,opacity:idx===0?0.35:1}}
                    onClick={e=>{e.stopPropagation();moveNavItem(idx,-1);}}
                    disabled={idx===0}
                    title="Monter"
                  >↑</button>
                  <button
                    type="button"
                    className="btn bg bsm"
                    style={{padding:"2px 8px",minWidth:0,fontSize:13,opacity:idx===orderedNav.length-1?0.35:1}}
                    onClick={e=>{e.stopPropagation();moveNavItem(idx,1);}}
                    disabled={idx===orderedNav.length-1}
                    title="Descendre"
                  >↓</button>
                </span>
              )}
            </div>
          ))}
        </nav>
        <div className="sb-bot">
          <div className="user-info">
            <div className="user-avatar">{user.fullName?.charAt(0)}</div>
            <div className="user-detail">
              <div className="user-name">{user.fullName}</div>
              <div className="user-role">{user.role}</div>
            </div>
          </div>
          <div className="ni" onClick={()=>{setShowPwModal(true);setMenuOpen(false);}}>
            <span className="ni-ico">🔒</span>Mot de passe
          </div>
          <div className="ni" onClick={()=>{setShowRateModal(true);setMenuOpen(false);}}>
            <span className="ni-ico">⏱</span>Taux horaire
          </div>
          <div className="ni" onClick={()=>setEditingNav(!editingNav)}>
            <span className="ni-ico">{editingNav?"✓":"⇅"}</span>
            <span style={{flex:1}}>{editingNav?"Terminer":"Réorganiser"}</span>
            {editingNav && navOrder.length>0 && (
              <button
                type="button"
                className="btn bg bsm"
                style={{padding:"2px 8px",minWidth:0,fontSize:11}}
                onClick={e=>{e.stopPropagation();resetNavOrder();}}
                title="Réinitialiser l'ordre par défaut"
              >Défaut</button>
            )}
          </div>
          <div className="ni" onClick={onLogout}>
            <span className="ni-ico">⏻</span>Sign Out
          </div>
        </div>
      </aside>

      <main className="main">
        <div className="topbar">
          <div style={{display:"flex",alignItems:"center"}}>
            <button className="burger" onClick={()=>setMenuOpen(!menuOpen)}>☰</button>
            <div className="topbar-title">{NAV.find(n=>n.id===view)?.lbl}</div>
          </div>
          <div className="topbar-right">
            {["contacts","archived","tasks"].includes(view) && <div style={{position:"relative",display:"inline-flex",alignItems:"center"}}>
              <input className="srch" placeholder="Search..." value={search} onChange={e=>setSearch(e.target.value)} style={search?{paddingRight:24}:{}} />
              {search && <button onClick={()=>setSearch("")} style={{position:"absolute",right:6,background:"none",border:"none",cursor:"pointer",color:"var(--t3)",fontSize:14,lineHeight:1,padding:0}}>✕</button>}
            </div>}
            {view==="contacts" && <>
              <button className="btn bg bsm" onClick={()=>exportContacts(contacts.filter(c=>c.status!=="Archived"),"clients-actifs.csv")} title="Exporter">📥 Export</button>
              <button className="btn bp" onClick={()=>setModal({type:"contact",data:null})}>+ Client</button>
            </>}
            {view==="archived" && <button className="btn bg bsm" onClick={()=>exportContacts(contacts.filter(c=>c.status==="Archived"),"clients-archives.csv")} title="Exporter">📥 Export</button>}
            {view==="pipeline" && <button className="btn bp" onClick={()=>setModal({type:"deal",data:null})}>+ Deal</button>}
            {view==="tasks" && <button className="btn bp" onClick={()=>setModal({type:"task",data:null})}>+ Task</button>}
          </div>
        </div>

        <div className="content">
          {view==="dashboard" && <Dashboard contacts={contacts} deals={dealsList} tasks={tasksList} pipeVal={pipeVal} wonVal={wonVal} pending={pending} overdue={overdue} cName={cName} setView={setView} />}
          {view==="contacts" && <Contacts contacts={filtC} onEdit={c=>setModal({type:"contact",data:c})} onDelete={deleteContact} onArchive={archiveContact} onColorTag={colorTagContact} onResetAllColors={resetAllColors} onBulkColorTag={bulkColorTag} mode="active" />}
          {view==="archived" && <Contacts contacts={filtA} onEdit={c=>setModal({type:"contact",data:c})} onDelete={deleteContact} onArchive={archiveContact} onColorTag={colorTagContact} onResetAllColors={resetAllColors} onBulkColorTag={bulkColorTag} mode="archived" />}
          {view==="map" && <ClientMap contacts={contacts.filter(c=>c.status!=="Archived")} />}
          {view==="revenue" && <Revenue contacts={contacts} />}
          {view==="calculator" && <PriceCalculator />}
          {view==="taxes" && <TaxCalculator />}
          {view==="pipeline" && <Pipeline deals={dealsList} onEdit={d=>setModal({type:"deal",data:d})} onMove={moveDeal} onDelete={deleteDeal} cName={cName} />}
          {view==="tasks" && <Tasks tasks={filtT} onToggle={toggleTask} onEdit={t=>setModal({type:"task",data:t})} onDelete={deleteTask} cName={cName} />}
          {view==="calendrier" && <Calendar jobs={scheduleJobs} contacts={contacts} onCreate={createJob} onUpdate={updateJob} onMove={moveJob} onDelete={deleteJob} notify={notify} />}
        </div>
      </main>

      {modal && <FormModal type={modal.type} data={modal.data} contacts={contacts} onClose={()=>setModal(null)} onSave={handleSave} />}
      {showPwModal && <PasswordModal onClose={()=>setShowPwModal(false)} notify={notify} />}
      {showRateModal && <RateModal onClose={()=>setShowRateModal(false)} notify={notify} />}
      {notif && <div className={`notif ${notif.type}`}>{notif.msg}</div>}
    </div>
  );
}

// ── Dashboard ──────────────────────────────────────────────────────────────────
function Dashboard({contacts,deals,tasks,pipeVal,wonVal,pending,overdue,cName,setView}) {
  const topDeals = [...deals].filter(d=>!["Closed Won","Closed Lost"].includes(d.stage)).sort((a,b)=>b.value-a.value).slice(0,5);
  const urgTasks = tasks.filter(t=>!t.completed&&["Urgent","High"].includes(t.priority)).slice(0,5);
  return (
    <>
      <div className="kgrid">
        <div className="kcard"><div className="klbl">Pipeline Value</div><div className="kval blu">{fmt(pipeVal)}</div><div className="ksub">{deals.filter(d=>!["Closed Won","Closed Lost"].includes(d.stage)).length} active deals</div></div>
        <div className="kcard"><div className="klbl">Closed Won</div><div className="kval grn">{fmt(wonVal)}</div><div className="ksub">{deals.filter(d=>d.stage==="Closed Won").length} deals won</div></div>
        <div className="kcard"><div className="klbl">Total Contacts</div><div className="kval">{contacts.length}</div><div className="ksub">{contacts.filter(c=>c.status==="Active").length} active</div></div>
        <div className="kcard"><div className="klbl">Open Tasks</div><div className={`kval ${overdue>0?"rd":""}`}>{pending}</div><div className="ksub" style={{color:overdue>0?"#f87171":""}}>{overdue>0?`${overdue} overdue`:"All on track"}</div></div>
      </div>
      <div className="dgrid">
        <div className="card">
          <div className="sh"><div className="st">Top Deals</div><button className="btn bg bsm" onClick={()=>setView("pipeline")}>View all →</button></div>
          {topDeals.map(d => (
            <div key={d.id} className="dash-row">
              <div style={{flex:1}}><div className="dash-name">{d.title}</div><div className="dash-sub">{d.contactName||cName(d.contactId)}</div></div>
              <div style={{textAlign:"right"}}><div className="dash-val">{fmt(d.value)}</div><span className="tag-sm" style={{background:STAGE_COLORS[d.stage]+"22",color:STAGE_COLORS[d.stage],border:`1px solid ${STAGE_COLORS[d.stage]}44`}}>{d.stage}</span></div>
            </div>
          ))}
          {topDeals.length===0 && <div className="empty"><div className="eico">◧</div>No active deals</div>}
        </div>
        <div className="card">
          <div className="sh"><div className="st">Priority Tasks</div><button className="btn bg bsm" onClick={()=>setView("tasks")}>View all →</button></div>
          {urgTasks.length===0 && <div className="empty"><div className="eico">✓</div>No urgent tasks</div>}
          {urgTasks.map(t => (
            <div key={t.id} className="dash-row">
              <div style={{width:6,height:6,borderRadius:"50%",background:PRIORITY_COLORS[t.priority],flexShrink:0}} />
              <div style={{flex:1}}><div className="dash-name">{t.title}</div><div className="dash-sub" style={{color:isOverdue(t.dueDate)?"#f87171":""}}>Due {fmtDate(t.dueDate)}{isOverdue(t.dueDate)?" ⚠ Overdue":""}</div></div>
              <span className="tag-sm" style={{background:PRIORITY_COLORS[t.priority]+"22",color:PRIORITY_COLORS[t.priority],border:`1px solid ${PRIORITY_COLORS[t.priority]}44`}}>{t.priority}</span>
            </div>
          ))}
        </div>
      </div>
    </>
  );
}

// ── Contacts ───────────────────────────────────────────────────────────────────
// Note parsers
const parseNotes = notes => {
  if(!notes) return {prix:0,prixOut:0,prixAutre:"",duree:"",rest:""};
  let prix=0, prixOut=0, prixAutre="", duree="", rest=[];
  (notes.split("|")).forEach(p=>{
    const t=p.trim();
    const mp = t.match(/^Prix:\s*\$?([\d,]+)/); if(mp){prix=parseInt(mp[1].replace(",",""));return;}
    const mo = t.match(/^Prix OUT:\s*\$?([\d,]+)/); if(mo){prixOut=parseInt(mo[1].replace(",",""));return;}
    const ma = t.match(/^Autre:\s*(.*)/); if(ma){prixAutre=ma[1].trim();return;}
    const md = t.match(/^Dur[ée]e:\s*(.*)/i); if(md){duree=md[1].trim();return;}
    if(t) rest.push(t);
  });
  return {prix,prixOut,prixAutre,duree,rest:rest.join(" | ")};
};
const buildNotes = (prix,prixOut,prixAutre,duree,rest) => {
  const parts = [];
  if(prix) parts.push(`Prix: $${prix}`);
  if(prixOut) parts.push(`Prix OUT: $${prixOut}`);
  if(prixAutre) parts.push(`Autre: ${prixAutre}`);
  if(duree) parts.push(`Durée: ${duree}`);
  if(rest) parts.push(rest);
  return parts.join(" | ");
};
const getHourlyRate = () => {
  try { return parseFloat(localStorage.getItem("ll_hourly_rate")) || 150; } catch { return 150; }
};
const autoEstimate = (prix) => {
  if(!prix) return "";
  const rate = getHourlyRate();
  const hours = prix / rate;
  if(hours < 0.5) return "~15min";
  if(hours < 0.75) return "~30min";
  if(hours < 1) return "~45min";
  const h = Math.floor(hours);
  const m = Math.round((hours - h) * 60 / 15) * 15;
  if(m === 0) return `~${h}h`;
  return `~${h}h${m.toString().padStart(2,"0")}`;
};

function Contacts({contacts,onEdit,onDelete,onArchive,onColorTag,onResetAllColors,onBulkColorTag,mode="active"}) {
  const [expanded,setExpanded] = useState(null);
  const [sortKey,setSortKey] = useState(()=>{
    try{const s=localStorage.getItem(`ll_sort_${mode}`);if(s){const p=JSON.parse(s);return p.key;}}catch{}return "fullName";
  });
  const [sortDir,setSortDir] = useState(()=>{
    try{const s=localStorage.getItem(`ll_sort_${mode}`);if(s){const p=JSON.parse(s);return p.dir;}}catch{}return "asc";
  });
  const [filterColors,setFilterColors] = useState(new Set());
  const [lastChange,setLastChange] = useState(null);
  const [confirmReset,setConfirmReset] = useState(false);
  const [selectMode,setSelectMode] = useState(false);
  const [selected,setSelected] = useState({});
  const [confirmDelete,setConfirmDelete] = useState(null);
  const lastClickedIdx = useRef(null);
  const statusClass = s => s==="Active"?"ta":s==="Lead"?"tl":s==="Archived"?"tg":"tc";

  const COLOR_TAGS = {
    booked:{label:"Booked",color:"#4ade80",bg:"rgba(74,222,128,.15)",border:"rgba(74,222,128,.35)"},
    waiting:{label:"Waiting",color:"#f5a623",bg:"rgba(245,166,35,.15)",border:"rgba(245,166,35,.35)"},
    quote:{label:"Quote",color:"#60a5fa",bg:"rgba(96,165,250,.15)",border:"rgba(96,165,250,.35)"},
    associate:{label:"Associé",color:"#a78bfa",bg:"rgba(167,139,250,.15)",border:"rgba(167,139,250,.35)"},
  };

  const getColorTag = c => {
    if(!c.tags) return null;
    return (c.tags||[]).find(t=>["booked","waiting","quote","associate"].includes(t)) || null;
  };

  const handleColorChange = (id, newTag) => {
    const contact = contacts.find(c=>c.id===id);
    const oldTag = getColorTag(contact);
    setLastChange({id, oldTag, name: contact?.fullName});
    onColorTag(id, newTag || null);
    // Auto-clear undo after 8 seconds
    setTimeout(()=>setLastChange(prev=>prev?.id===id?null:prev), 8000);
  };

  const undoLastChange = () => {
    if(!lastChange) return;
    onColorTag(lastChange.id, lastChange.oldTag);
    setLastChange(null);
  };

  const toggleSelect = (id, idx, shiftKey) => {
    if(shiftKey && lastClickedIdx.current !== null && lastClickedIdx.current !== idx) {
      const start = Math.min(lastClickedIdx.current, idx);
      const end = Math.max(lastClickedIdx.current, idx);
      setSelected(s => {
        const next = {...s};
        for(let i = start; i <= end; i++) {
          if(sorted[i]) next[sorted[i].id] = true;
        }
        return next;
      });
    } else {
      setSelected(s=>({...s,[id]:!s[id]}));
    }
    lastClickedIdx.current = idx;
  };
  const selectAll = () => {
    const all = {};
    sorted.forEach(c=>all[c.id]=true);
    setSelected(all);
    lastClickedIdx.current = null;
  };
  const selectNone = () => { setSelected({}); lastClickedIdx.current = null; };
  const selectedCount = Object.values(selected).filter(Boolean).length;
  const selectedIds = Object.entries(selected).filter(([_,v])=>v).map(([k])=>k);

  const applyBulkColor = tag => {
    onBulkColorTag(selectedIds, tag);
    setSelected({});
    setSelectMode(false);
  };

  const exitSelectMode = () => {
    setSelectMode(false);
    setSelected({});
  };

  const handleSort = (key, forceDir) => {
    let newKey, newDir;
    if(forceDir) { newKey=key; newDir=forceDir; }
    else if(sortKey===key) { newKey=key; newDir=sortDir==="asc"?"desc":"asc"; }
    else { newKey=key; newDir="asc"; }
    setSortKey(newKey); setSortDir(newDir);
    try{localStorage.setItem(`ll_sort_${mode}`,JSON.stringify({key:newKey,dir:newDir}));}catch{}
  };
  const resetSort = () => {
    setSortKey("fullName"); setSortDir("asc");
    try{localStorage.removeItem(`ll_sort_${mode}`);}catch{}
  };
  const isDefaultSort = sortKey==="fullName"&&sortDir==="asc";
  const arrow = key => sortKey===key ? (sortDir==="asc"?" ▲":" ▼") : "";

  const filtered = filterColors.size===0 ? contacts : contacts.filter(c=>{
    const ct=getColorTag(c);
    if(filterColors.has("none") && !ct) return true;
    return ct && filterColors.has(ct);
  });

  const sorted = [...filtered].sort((a,b)=>{
    let va,vb;
    const pa = parseNotes(a.notes), pb = parseNotes(b.notes);
    if(sortKey==="prix"){ va=pa.prix; vb=pb.prix; }
    else if(sortKey==="prixOut"){ va=pa.prixOut; vb=pb.prixOut; }
    else if(sortKey==="prixAutre"){ va=(pa.prixAutre||"").toLowerCase(); vb=(pb.prixAutre||"").toLowerCase(); }
    else if(sortKey==="createdAt"){ va=a.createdAt||""; vb=b.createdAt||""; }
    else { va=(a[sortKey]||"").toLowerCase(); vb=(b[sortKey]||"").toLowerCase(); }
    if(va<vb) return sortDir==="asc"?-1:1;
    if(va>vb) return sortDir==="asc"?1:-1;
    return 0;
  });

  const isArchived = mode==="archived";
  const pStyle = {color:"var(--pri)",fontWeight:600,fontFamily:"'Outfit',sans-serif",fontSize:12};

  // Count colors
  const colorCounts = {booked:0,waiting:0,quote:0,associate:0,none:0};
  const totalTagged = contacts.reduce((s,c)=>{const t=getColorTag(c); if(t){colorCounts[t]++;return s+1;} colorCounts.none++;return s;},0);

  return (
    <div className="card">
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12,flexWrap:"wrap",gap:8}}>
        <div style={{fontSize:10,color:"var(--t3)"}}>
          {filtered.length} {isArchived?"archived":"active"} client{filtered.length!==1?"s":""}
        </div>
        <div style={{display:"flex",gap:4,alignItems:"center",flexWrap:"wrap"}}>
          <button className={`btn bsm ${filterColors.size===0?"bp":"bg"}`} onClick={()=>setFilterColors(new Set())}>All</button>
          {Object.entries(COLOR_TAGS).map(([key,ct])=>{
            const active=filterColors.has(key);
            return(
            <button key={key} className={`btn bsm ${active?"bp":"bg"}`}
              style={active?{background:ct.color,borderColor:ct.color,color:"#000"}:{borderColor:ct.border,color:ct.color}}
              onClick={()=>setFilterColors(prev=>{const n=new Set(prev);n.has(key)?n.delete(key):n.add(key);return n;})}>
              ● {ct.label} ({colorCounts[key]})
            </button>);
          })}
        {colorCounts.none>0 && (
          <button className={`btn bsm ${filterColors.has("none")?"bp":"bg"}`}
            onClick={()=>setFilterColors(prev=>{const n=new Set(prev);n.has("none")?n.delete("none"):n.add("none");return n;})}>
            ⬜ Aucune couleur ({colorCounts.none})
          </button>)}
          {totalTagged>0 && !confirmReset && (
            <button className="btn bd bsm" onClick={()=>setConfirmReset(true)} title="Nouvelle saison — effacer tous les codes couleur">
              ↻ Reset ({totalTagged})
            </button>
          )}
          <button className={`btn ${selectMode?"bp":"bg"} bsm`} onClick={()=>selectMode?exitSelectMode():setSelectMode(true)}>
            {selectMode?"✕ Annuler":"☐ Sélectionner"}
          </button>
          {!isDefaultSort && <button className="btn bg bsm" onClick={resetSort} title="Réinitialiser le tri">↺</button>}
          <div className="mob-sort">
            <select className="fi" style={{width:"auto",padding:"4px 8px",fontSize:10}} value={`${sortKey}_${sortDir}`} onChange={e=>{
              if(e.target.value==="reset") { resetSort(); return; }
              const [k,d] = e.target.value.split("_");
              handleSort(k,d);
            }}>
              <option value="reset">↺ Réinitialiser</option>
              <option value="fullName_asc">Nom A→Z</option>
              <option value="fullName_desc">Nom Z→A</option>
              <option value="company_asc">Adresse A→Z</option>
              <option value="prix_desc">Prix ↑ haut</option>
              <option value="prix_asc">Prix ↓ bas</option>
              <option value="prixOut_desc">Prix OUT ↑</option>
              <option value="createdAt_desc">Plus récent</option>
              <option value="createdAt_asc">Plus ancien</option>
            </select>
          </div>
        </div>
      </div>

      {/* Confirm reset all */}
      {confirmReset && (
        <div style={{background:"rgba(248,113,113,.08)",border:"1px solid rgba(248,113,113,.3)",borderRadius:"var(--r)",padding:"12px 16px",marginBottom:12,display:"flex",alignItems:"center",justifyContent:"space-between",flexWrap:"wrap",gap:8}}>
          <div style={{fontSize:12,color:"var(--t1)"}}>
            Réinitialiser les <strong>{totalTagged}</strong> codes couleur? (Nouvelle saison)
          </div>
          <div style={{display:"flex",gap:6}}>
            <button className="btn bd bsm" onClick={()=>{onResetAllColors();setConfirmReset(false);}}>Oui, réinitialiser tout</button>
            <button className="btn bg bsm" onClick={()=>setConfirmReset(false)}>Annuler</button>
          </div>
        </div>
      )}

      {/* Undo toast */}
      {lastChange && (
        <div style={{background:"var(--s2)",border:"1px solid var(--b2)",borderRadius:"var(--r)",padding:"8px 14px",marginBottom:10,display:"flex",alignItems:"center",justifyContent:"space-between",gap:8}}>
          <div style={{fontSize:11,color:"var(--t2)"}}>
            Couleur changée pour <strong style={{color:"var(--t1)"}}>{lastChange.name}</strong>
          </div>
          <button className="btn bg bsm" onClick={undoLastChange} style={{color:"var(--pri)",borderColor:"var(--pri)"}}>↩ Annuler</button>
        </div>
      )}

      {/* Bulk action bar */}
      {selectMode && (
        <div style={{background:"var(--pri-a)",border:"1px solid rgba(232,184,23,.3)",borderRadius:"var(--r)",padding:"10px 14px",marginBottom:10,display:"flex",alignItems:"center",justifyContent:"space-between",flexWrap:"wrap",gap:8}}>
          <div style={{display:"flex",alignItems:"center",gap:8}}>
            <button className="btn bg bsm" onClick={selectAll}>Tout</button>
            <button className="btn bg bsm" onClick={selectNone}>Aucun</button>
            <span style={{fontSize:11,color:"var(--t2)"}}>{selectedCount} sélectionné{selectedCount>1?"s":""}</span>
            <span style={{fontSize:9,color:"var(--t3)"}}>· Shift+clic pour sélectionner une plage</span>
          </div>
          {selectedCount>0 && (
            <div style={{display:"flex",alignItems:"center",gap:4,flexWrap:"wrap"}}>
              <span style={{fontSize:10,color:"var(--t3)",marginRight:4}}>Appliquer:</span>
              <button className="btn bsm" style={{background:"rgba(74,222,128,.15)",borderColor:"rgba(74,222,128,.35)",color:"#4ade80"}} onClick={()=>applyBulkColor("booked")}>🟢 Booked</button>
              <button className="btn bsm" style={{background:"rgba(245,166,35,.15)",borderColor:"rgba(245,166,35,.35)",color:"#f5a623"}} onClick={()=>applyBulkColor("waiting")}>🟡 Waiting</button>
              <button className="btn bsm" style={{background:"rgba(96,165,250,.15)",borderColor:"rgba(96,165,250,.35)",color:"#60a5fa"}} onClick={()=>applyBulkColor("quote")}>🔵 Quote</button>
              <button className="btn bsm" style={{background:"rgba(167,139,250,.15)",borderColor:"rgba(167,139,250,.35)",color:"#a78bfa"}} onClick={()=>applyBulkColor("associate")}>🟣 Associé</button>
              <button className="btn bg bsm" onClick={()=>applyBulkColor(null)}>— Clear</button>
            </div>
          )}
        </div>
      )}
      <div className="twrap"><table><thead><tr>
        {selectMode && <th style={{width:30}}></th>}
        <th style={{width:30}}></th>
        <th style={{cursor:"pointer"}} onClick={()=>handleSort("fullName")} onDoubleClick={resetSort}>Name{arrow("fullName")}</th>
        <th style={{cursor:"pointer"}} onClick={()=>handleSort("company")} onDoubleClick={resetSort}>Address{arrow("company")}</th>
        <th style={{cursor:"pointer"}} onClick={()=>handleSort("phone")} onDoubleClick={resetSort}>Phone{arrow("phone")}</th>
        <th style={{cursor:"pointer"}} onClick={()=>handleSort("prix")} onDoubleClick={resetSort}>Prix{arrow("prix")}</th>
        <th style={{cursor:"pointer"}} onClick={()=>handleSort("prixOut")} onDoubleClick={resetSort}>Prix OUT{arrow("prixOut")}</th>
        <th style={{cursor:"pointer"}} onClick={()=>handleSort("prixAutre")} onDoubleClick={resetSort}>Prix Autre{arrow("prixAutre")}</th>
        <th style={{cursor:"pointer"}} onClick={()=>handleSort("createdAt")} onDoubleClick={resetSort}>Date{arrow("createdAt")}</th>
        <th>Durée</th>
        <th></th>
      </tr></thead>
      <tbody>{sorted.map((c,rowIdx) => {
        const p = parseNotes(c.notes);
        const ct = getColorTag(c);
        const ctInfo = ct ? COLOR_TAGS[ct] : null;
        const rowBorder = ctInfo ? `2px solid ${ctInfo.color}` : "none";
        return (
        <React.Fragment key={c.id}>
          <tr style={{cursor:"pointer",borderLeft:rowBorder,background:selected[c.id]?"var(--pri-a)":"transparent"}} onClick={(e)=>selectMode?toggleSelect(c.id,rowIdx,e.shiftKey):setExpanded(expanded===c.id?null:c.id)}>
            {selectMode && (
              <td style={{padding:"4px 6px",verticalAlign:"middle"}} onClick={e=>e.stopPropagation()}>
                <div className={`tck ${selected[c.id]?"dn":""}`} onClick={(e)=>toggleSelect(c.id,rowIdx,e.shiftKey)} style={{width:20,height:20}}>
                  {selected[c.id] && <span style={{color:"#0a0a0a",fontSize:11,fontWeight:700}}>✓</span>}
                </div>
              </td>
            )}
            <td style={{padding:"4px 6px",verticalAlign:"middle"}} onClick={e=>e.stopPropagation()}>
              <div style={{position:"relative",display:"inline-block"}}>
                <select style={{appearance:"none",background:ctInfo?ctInfo.bg:"var(--s2)",border:`1px solid ${ctInfo?ctInfo.border:"var(--b1)"}`,borderRadius:4,padding:"2px 4px",fontSize:12,cursor:"pointer",color:ctInfo?ctInfo.color:"var(--t3)",width:28,height:24,textAlign:"center"}}
                  value={ct||""} onChange={e=>{handleColorChange(c.id,e.target.value||null);}}>
                  <option value="">—</option>
                  <option value="booked" style={{color:"#4ade80"}}>🟢</option>
                  <option value="waiting" style={{color:"#f5a623"}}>🟡</option>
                  <option value="quote" style={{color:"#60a5fa"}}>🔵</option>
                  <option value="associate" style={{color:"#a78bfa"}}>🟣</option>
                </select>
              </div>
            </td>
            <td className="tdn">{c.fullName}</td>
            <td data-label="Address" style={{maxWidth:180,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{c.company}</td>
            <td data-label="Phone"><a href={`tel:${c.phone}`} onClick={e=>e.stopPropagation()}>{c.phone||"—"}</a></td>
            <td data-label="Prix" style={pStyle}>{p.prix ? `$${p.prix}` : "—"}</td>
            <td data-label="Prix OUT" style={{...pStyle,color:p.prixOut?"#60a5fa":"var(--t3)"}}>{p.prixOut ? `$${p.prixOut}` : "—"}</td>
            <td data-label="Autre" className="mob-hide" style={{maxWidth:150,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",fontSize:10,color:"var(--t2)"}}>{p.prixAutre||"—"}</td>
            <td data-label="Date" style={{fontSize:10,color:"var(--t3)"}}>{c.createdAt ? new Date(c.createdAt).toLocaleDateString("fr-CA",{day:"numeric",month:"short"}) : "—"}</td>
            <td data-label="Durée" style={{fontSize:11,color:p.duree?"var(--grn)":"var(--t3)"}}>{p.duree||autoEstimate(p.prix)||"—"}</td>
            <td><div className="ra">
              {isArchived ? (<>
                <button className="btn bs bsm" style={{background:"rgba(74,222,128,.12)",borderColor:"rgba(74,222,128,.28)",color:"#4ade80"}} title="Restore" onClick={e=>{e.stopPropagation();onArchive(c.id,"Active");}}>↩</button>
                <button className="btn bg bsm" onClick={e=>{e.stopPropagation();onEdit(c);}}>Edit</button>
                {confirmDelete===c.id ? (<>
                  <button className="btn bd bsm" onClick={e=>{e.stopPropagation();onDelete(c.id);setConfirmDelete(null);}}>Confirmer</button>
                  <button className="btn bg bsm" onClick={e=>{e.stopPropagation();setConfirmDelete(null);}}>Non</button>
                </>) : (
                  <button className="btn bd bsm" onClick={e=>{e.stopPropagation();setConfirmDelete(c.id);}}>Del</button>
                )}
              </>) : (<>
                <button className="btn bg bsm" title="Archive" onClick={e=>{e.stopPropagation();onArchive(c.id);}}>📦</button>
                <button className="btn bg bsm" onClick={e=>{e.stopPropagation();onEdit(c);}}>Edit</button>
              </>)}
            </div></td>
          </tr>
          {expanded===c.id && (
            <tr><td colSpan={selectMode?11:10} style={{background:"var(--s2)",padding:16,borderBottom:"1px solid var(--b1)"}}>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:14}}>
                <div>
                  <div style={{fontSize:9.5,color:"var(--t3)",textTransform:"uppercase",letterSpacing:1,marginBottom:4}}>Full Address</div>
                  <div style={{fontSize:12,color:"var(--t1)"}}>{c.company||"—"}</div>
                </div>
                <div>
                  <div style={{fontSize:9.5,color:"var(--t3)",textTransform:"uppercase",letterSpacing:1,marginBottom:4}}>Contact</div>
                  <div style={{fontSize:12,color:"var(--t1)"}}>{c.phone||"—"}</div>
                  <div style={{fontSize:12,color:"var(--t2)",marginTop:2}}>{c.email||"—"}</div>
                </div>
                <div>
                  <div style={{fontSize:9.5,color:"var(--t3)",textTransform:"uppercase",letterSpacing:1,marginBottom:4}}>Pricing</div>
                  <div style={{fontSize:12,color:"var(--pri)"}}>In/Out: {p.prix?`$${p.prix}`:"—"}</div>
                  <div style={{fontSize:12,color:"#60a5fa",marginTop:2}}>Out: {p.prixOut?`$${p.prixOut}`:"—"}</div>
                  {p.prixAutre && <div style={{fontSize:11,color:"var(--t2)",marginTop:4}}>{p.prixAutre}</div>}
                  <div style={{fontSize:11,color:p.duree?"var(--grn)":"var(--t3)",marginTop:4}}>⏱ {p.duree||autoEstimate(p.prix)||"—"} {!p.duree&&p.prix?<span style={{fontSize:9,color:"var(--t3)"}}>(auto)</span>:""}</div>
                </div>
              </div>
              {p.rest && (
                <div style={{marginTop:14}}>
                  <div style={{fontSize:9.5,color:"var(--t3)",textTransform:"uppercase",letterSpacing:1,marginBottom:4}}>Notes</div>
                  <div style={{fontSize:12,color:"var(--t1)",lineHeight:1.7,whiteSpace:"pre-wrap"}}>{p.rest.replace(/\s*\|\s*/g, "\n")}</div>
                </div>
              )}
            </td></tr>
          )}
        </React.Fragment>
        );
      })}</tbody></table>
      {filtered.length===0 && <div className="empty"><div className="eico">◉</div>{isArchived?"No archived clients":"No clients found"}</div>}
    </div></div>
  );
}

// ── Client Map ─────────────────────────────────────────────────────────────────
function ClientMap({contacts}) {
  const mapRef = useRef(null);
  const mapInstance = useRef(null);
  const markersRef = useRef([]);
  const [geocoded, setGeocoded] = useState(0);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const COLOR_MAP = {booked:"#4ade80",waiting:"#f5a623",quote:"#60a5fa",associate:"#a78bfa"};
  const getColorTag = c => (c.tags||[]).find(t=>["booked","waiting","quote","associate"].includes(t))||null;
  const getPrix = notes => { const m=(notes||"").match(/Prix:\s*\$?([\d,]+)/); return m?`$${m[1]}`:""; };

  useEffect(()=>{
    // Load Leaflet CSS
    if(!document.getElementById("leaflet-css")){
      const link=document.createElement("link");
      link.id="leaflet-css";
      link.rel="stylesheet";
      link.href="https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/leaflet.min.css";
      document.head.appendChild(link);
    }

    // Load Leaflet JS
    const loadLeaflet = () => new Promise(resolve=>{
      if(window.L){resolve();return;}
      const s=document.createElement("script");
      s.src="https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/leaflet.min.js";
      s.onload=resolve;
      document.head.appendChild(s);
    });

    const geocodeAddress = async (address) => {
      const cacheKey = `geo_${address.replace(/[^a-zA-Z0-9]/g,"_").slice(0,80)}`;
      try {
        const cached = localStorage.getItem(cacheKey);
        if(cached) return JSON.parse(cached);
      } catch{}

      try {
        const q = encodeURIComponent(address + ", Quebec, Canada");
        const res = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${q}&limit=1`);
        const data = await res.json();
        if(data&&data[0]){
          const coords = {lat:parseFloat(data[0].lat),lng:parseFloat(data[0].lon)};
          try{localStorage.setItem(cacheKey,JSON.stringify(coords));}catch{}
          return coords;
        }
      } catch{}
      return null;
    };

    const sleep = ms => new Promise(r=>setTimeout(r,ms));

    const init = async () => {
      await loadLeaflet();
      const L = window.L;

      if(!mapRef.current) return;
      if(mapInstance.current){mapInstance.current.remove();}

      const map = L.map(mapRef.current).setView([45.13,-72.08],10);
      mapInstance.current = map;

      L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",{
        attribution:'© OpenStreetMap',
        maxZoom:18
      }).addTo(map);

      const clientsWithAddr = contacts.filter(c=>c.company&&c.company!=="N/A"&&c.company.trim().length>3);
      setTotal(clientsWithAddr.length);
      setLoading(true);

      const bounds = [];
      let count = 0;

      for(const c of clientsWithAddr){
        const cacheKey = `geo_${c.company.replace(/[^a-zA-Z0-9]/g,"_").slice(0,80)}`;
        let wasCached = false;
        try { wasCached = !!localStorage.getItem(cacheKey); } catch{}

        const coords = await geocodeAddress(c.company);

        if(coords){
          const ct = getColorTag(c);
          const color = ct ? COLOR_MAP[ct] : "#e8b817";
          const prix = getPrix(c.notes);

          const icon = L.divIcon({
            className:'',
            html:`<div style="width:14px;height:14px;border-radius:50%;background:${color};border:2px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,.4);"></div>`,
            iconSize:[14,14],
            iconAnchor:[7,7]
          });

          const marker = L.marker([coords.lat,coords.lng],{icon}).addTo(map);
          marker.bindPopup(`
            <div style="font-family:Outfit,sans-serif;min-width:160px;">
              <div style="font-weight:600;font-size:14px;margin-bottom:4px;">${c.fullName}</div>
              <div style="font-size:12px;color:#666;margin-bottom:2px;">${c.company}</div>
              ${c.phone?`<div style="font-size:12px;margin-bottom:2px;"><a href="tel:${c.phone}" style="color:#4f8ef7;">${c.phone}</a></div>`:""}
              ${prix?`<div style="font-size:13px;font-weight:600;color:#e8b817;margin-top:4px;">${prix}</div>`:""}
              ${ct?`<div style="font-size:11px;margin-top:4px;padding:2px 6px;border-radius:4px;display:inline-block;background:${color}22;color:${color};">${ct==="booked"?"Booked":ct==="waiting"?"Waiting":ct==="quote"?"Quote":"Associé"}</div>`:""}
            </div>
          `);
          markersRef.current.push(marker);
          bounds.push([coords.lat,coords.lng]);
        }
        count++;
        setGeocoded(count);

        // Only rate limit when we actually hit the API (not cached)
        if(!wasCached) await sleep(1100);
      }

      if(bounds.length>0) map.fitBounds(bounds,{padding:[30,30]});
      setLoading(false);
    };

    init();

    return ()=>{
      if(mapInstance.current){mapInstance.current.remove();mapInstance.current=null;}
    };
  },[contacts]);

  return (
    <div>
      {loading && (
        <div style={{background:"var(--s2)",border:"1px solid var(--b1)",borderRadius:"var(--r)",padding:"10px 16px",marginBottom:10,display:"flex",alignItems:"center",gap:10}}>
          <div style={{width:16,height:16,border:"2px solid var(--pri)",borderTopColor:"transparent",borderRadius:"50%",animation:"spin 1s linear infinite"}}/>
          <span style={{fontSize:12,color:"var(--t2)"}}>Géolocalisation des clients... {geocoded}/{total}</span>
          <div style={{flex:1,height:3,background:"var(--b1)",borderRadius:2,overflow:"hidden"}}>
            <div style={{width:`${total?Math.round(geocoded/total*100):0}%`,height:"100%",background:"var(--pri)",transition:"width .3s"}}/>
          </div>
          <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
        </div>
      )}
      <div style={{display:"flex",gap:8,marginBottom:10,flexWrap:"wrap"}}>
        <div style={{display:"flex",alignItems:"center",gap:4,fontSize:11,color:"var(--t3)"}}>
          <div style={{width:10,height:10,borderRadius:"50%",background:"#e8b817"}}/>No tag
        </div>
        <div style={{display:"flex",alignItems:"center",gap:4,fontSize:11,color:"var(--t3)"}}>
          <div style={{width:10,height:10,borderRadius:"50%",background:"#4ade80"}}/>Booked
        </div>
        <div style={{display:"flex",alignItems:"center",gap:4,fontSize:11,color:"var(--t3)"}}>
          <div style={{width:10,height:10,borderRadius:"50%",background:"#f5a623"}}/>Waiting
        </div>
        <div style={{display:"flex",alignItems:"center",gap:4,fontSize:11,color:"var(--t3)"}}>
          <div style={{width:10,height:10,borderRadius:"50%",background:"#60a5fa"}}/>Quote
        </div>
        <div style={{display:"flex",alignItems:"center",gap:4,fontSize:11,color:"var(--t3)"}}>
          <div style={{width:10,height:10,borderRadius:"50%",background:"#a78bfa"}}/>Associé
        </div>
      </div>
      <div ref={mapRef} style={{width:"100%",height:"calc(100vh - 180px)",borderRadius:"var(--rl)",border:"1px solid var(--b1)",background:"var(--s2)"}}/>
    </div>
  );
}

// ── Revenue ────────────────────────────────────────────────────────────────────
function Revenue({contacts}) {
  const getPriceNum = notes => {
    if(!notes) return 0;
    const m = notes.match(/Prix:\s*\$?([\d,]+)/);
    return m ? parseInt(m[1].replace(",","")) : 0;
  };
  const getOutNum = notes => {
    if(!notes) return 0;
    const m = notes.match(/Prix OUT:\s*\$?([\d,]+)/);
    return m ? parseInt(m[1].replace(",","")) : 0;
  };
  const getFrequency = notes => {
    if(!notes) return 1;
    const n = notes.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g,""); // strip accents
    // Match: "2x par année", "2x/année", "2 fois par année", "2x par an", "2x annee", "2x année", "2x /an", "2x per year", "2x a year", "2x/an"
    const mf = n.match(/(\d+)\s*(?:x|fois)\s*[\/\s]*(?:par\s*)?(?:annee|anne|an\b|ans\b|a year|per year|year|par an)/);
    if(mf) return parseInt(mf[1]);
    // Just "2x" or "3x" by itself (common shorthand)
    const mx = n.match(/\b(\d+)\s*x\b/);
    if(mx && parseInt(mx[1])>1 && parseInt(mx[1])<=12) return parseInt(mx[1]);
    // "chaque mois", "every month", "aux mois", "par mois", "mensuel", "monthly"
    if(n.includes("chaque mois")||n.includes("every month")||n.includes("aux mois")||n.includes("par mois")||n.includes("mensuel")||n.includes("monthly")) return 12;
    // "aux 2 mois", "every 2 months", "aux 2 semaines"
    const mm = n.match(/(?:aux|every|chaque)\s*(\d+)\s*(?:mois|months)/);
    if(mm) return Math.round(12/parseInt(mm[1]));
    return 1;
  };
  const getFreqLabel = freq => {
    if(freq===12) return "Mensuel";
    if(freq>=6) return `${freq}x/an`;
    if(freq>1) return `${freq}x/an`;
    return "1x/an";
  };

  // Only active clients (exclude archived)
  const active = contacts.filter(c=>c.status!=="Archived");
  const withPrice = active.filter(c=>getPriceNum(c.notes)>0);

  // Per-visit totals
  const totalPerVisit = withPrice.reduce((s,c)=>s+getPriceNum(c.notes),0);
  const totalOutPerVisit = active.reduce((s,c)=>s+getOutNum(c.notes),0);

  // Annual totals (accounting for frequency)
  const clientData = withPrice.map(c=>{
    const prix = getPriceNum(c.notes);
    const prixOut = getOutNum(c.notes);
    const freq = getFrequency(c.notes);
    return { ...c, prix, prixOut, freq, annualIn: prix*freq, annualOut: prixOut*freq };
  });

  const totalAnnual = clientData.reduce((s,c)=>s+c.annualIn,0);
  const totalAnnualOut = clientData.reduce((s,c)=>s+c.annualOut,0);
  const avgPerClient = clientData.length ? Math.round(totalAnnual/clientData.length) : 0;

  // Breakdown by frequency
  const oneTime = clientData.filter(c=>c.freq===1);
  const recurring = clientData.filter(c=>c.freq>1);
  const recurringAnnual = recurring.reduce((s,c)=>s+c.annualIn,0);
  const oneTimeAnnual = oneTime.reduce((s,c)=>s+c.annualIn,0);

  // Top clients by annual revenue
  const topClients = [...clientData].sort((a,b)=>b.annualIn-a.annualIn).slice(0,15);

  // Region classifier
  const getRegion = addr => {
    if(!addr) return "Autre";
    const a = addr.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g,"");
    // Eastern Townships
    if(/(ogden|georgeville|geogeville|gerogeville|goergeville|magog|austin|bromont|orford|mansonville|owl.?s head|ayer.?s cliff|bolton|eastman|fitch bay|hatley|knowlton|lac brome|lac brome|lennoxville|potton|sherbrooke|sutton|shefford|bonsecours|greene|stanstead|north hatley|way.?s mills|ste.?catherine|iron hill|deauville|magoon|descente|lac manitou)/.test(a)) return "Eastern Townships";
    // Montreal
    if(/(montreal|mont.?royal|outremont|notre.?dame.?de.?grace|ndg|verdun|westmount|saint.?laurent|st.?laurent|anjou|kirkland|beaconsfield|plateau|griffintown|redpath|wilderton|bassins|boise|pins\s*o|h2v|h3c)/.test(a)) return "Montréal";
    // South Shore
    if(/(st.?lambert|saint.?lambert|brossard|greenfield|st.?bruno|longueuil|st.?jean|saint.?jean|st.?hubert|la prairie|candiac|chambly|carignan)/.test(a)) return "Rive-Sud";
    // Laval
    if(/laval/.test(a)) return "Laval";
    // North Shore
    if(/(terrebonne|ste.?agathe|laurentides|blainville|rosemere|mirabel|st.?jerome)/.test(a)) return "Rive-Nord";
    return "Autre";
  };
  const REGION_COLORS = {"Eastern Townships":"#4ade80","Montréal":"#60a5fa","Rive-Sud":"#f5a623","Laval":"#a78bfa","Rive-Nord":"#f87171","Autre":"#888"};

  // Group by city (detailed)
  const areas = {};
  clientData.forEach(c=>{
    const addr = c.company||"";
    const parts = addr.split(",");
    const city = parts.length>1 ? parts[parts.length-1].trim() : parts[0]?.trim() || "Inconnu";
    const region = getRegion(addr);
    if(!areas[city]) areas[city] = {count:0,perVisit:0,annual:0,region};
    areas[city].count++;
    areas[city].perVisit += c.prix;
    areas[city].annual += c.annualIn;
  });
  const areaList = Object.entries(areas).map(([city,d])=>({city,...d})).sort((a,b)=>b.annual-a.annual);

  // Group by region
  const regions = {};
  clientData.forEach(c=>{
    const region = getRegion(c.company||"");
    if(!regions[region]) regions[region] = {count:0,annual:0,cities:{}};
    regions[region].count++;
    regions[region].annual += c.annualIn;
    const parts = (c.company||"").split(",");
    const city = parts.length>1 ? parts[parts.length-1].trim() : parts[0]?.trim() || "?";
    if(!regions[region].cities[city]) regions[region].cities[city] = {count:0,annual:0};
    regions[region].cities[city].count++;
    regions[region].cities[city].annual += c.annualIn;
  });
  const regionList = Object.entries(regions).map(([name,d])=>({name,...d})).sort((a,b)=>b.annual-a.annual);
  const [areaView, setAreaView] = useState("region");
  const [expandedRegion, setExpandedRegion] = useState(null);

  return (
    <>
      <div className="kgrid">
        <div className="kcard">
          <div className="klbl">Revenu annuel estimé</div>
          <div className="kval blu">{fmt(totalAnnual)}</div>
          <div className="ksub">{clientData.length} clients actifs</div>
        </div>
        <div className="kcard">
          <div className="klbl">Revenu par visite</div>
          <div className="kval">{fmt(totalPerVisit)}</div>
          <div className="ksub">si tout le monde une fois</div>
        </div>
        <div className="kcard">
          <div className="klbl">Revenu récurrent</div>
          <div className="kval grn">{fmt(recurringAnnual)}</div>
          <div className="ksub">{recurring.length} clients récurrents</div>
        </div>
        <div className="kcard">
          <div className="klbl">Moyenne annuelle</div>
          <div className="kval">{fmt(avgPerClient)}</div>
          <div className="ksub">par client</div>
        </div>
      </div>

      {/* Revenue breakdown */}
      <div className="card" style={{marginBottom:14}}>
        <div className="st" style={{marginBottom:14}}>Répartition du revenu annuel</div>
        <div style={{display:"flex",gap:4,marginBottom:12,borderRadius:6,overflow:"hidden",height:28}}>
          {recurringAnnual>0 && <div style={{flex:recurringAnnual,background:"#4ade80",display:"flex",alignItems:"center",justifyContent:"center",fontSize:10,fontWeight:700,color:"#0a0a0a"}}>
            Récurrent {totalAnnual>0?Math.round(recurringAnnual/totalAnnual*100):0}%
          </div>}
          {oneTimeAnnual>0 && <div style={{flex:oneTimeAnnual,background:"var(--pri)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:10,fontWeight:700,color:"#0a0a0a"}}>
            Unique {totalAnnual>0?Math.round(oneTimeAnnual/totalAnnual*100):0}%
          </div>}
        </div>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:14,fontSize:12}}>
          <div>
            <div style={{color:"var(--t3)",fontSize:10,textTransform:"uppercase",letterSpacing:.5}}>Récurrent ({recurring.length} clients)</div>
            <div style={{color:"#4ade80",fontWeight:700,fontSize:16,marginTop:4}}>{fmt(recurringAnnual)}</div>
          </div>
          <div>
            <div style={{color:"var(--t3)",fontSize:10,textTransform:"uppercase",letterSpacing:.5}}>Unique ({oneTime.length} clients)</div>
            <div style={{color:"var(--pri)",fontWeight:700,fontSize:16,marginTop:4}}>{fmt(oneTimeAnnual)}</div>
          </div>
          <div>
            <div style={{color:"var(--t3)",fontSize:10,textTransform:"uppercase",letterSpacing:.5}}>Extérieur seulement (annuel)</div>
            <div style={{color:"#60a5fa",fontWeight:700,fontSize:16,marginTop:4}}>{fmt(totalAnnualOut)}</div>
          </div>
        </div>
      </div>

      <div className="dgrid">
        <div className="card">
          <div className="sh"><div className="st">Top 15 clients (annuel)</div></div>
          {topClients.map((c,i) => (
            <div key={c.id} style={{display:"flex",alignItems:"center",padding:"9px 0",borderBottom:"1px solid var(--b1)",gap:10}}>
              <div style={{width:22,height:22,borderRadius:"50%",background:"var(--pri-a)",color:"var(--pri)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:10,fontWeight:700,flexShrink:0}}>{i+1}</div>
              <div style={{flex:1}}>
                <div style={{fontSize:12,fontWeight:600,color:"var(--t1)"}}>{c.fullName}</div>
                <div style={{fontSize:10,color:"var(--t3)",marginTop:1}}>
                  {c.company}
                  {c.freq>1 && <span style={{marginLeft:6,color:c.freq>=6?"#4ade80":"#f5a623"}}>{getFreqLabel(c.freq)}</span>}
                </div>
              </div>
              <div style={{textAlign:"right"}}>
                <div style={{fontFamily:"'Outfit',sans-serif",fontSize:15,fontWeight:700,color:"var(--pri)"}}>{fmt(c.annualIn)}</div>
                {c.freq>1 && <div style={{fontSize:9,color:"var(--t3)"}}>{fmt(c.prix)}/visite</div>}
              </div>
            </div>
          ))}
        </div>

        <div className="card">
          <div className="sh">
            <div className="st">Revenu par secteur (annuel)</div>
            <div style={{display:"flex",gap:4}}>
              <button className={`btn bsm ${areaView==="region"?"bp":"bg"}`} onClick={()=>setAreaView("region")}>Régions</button>
              <button className={`btn bsm ${areaView==="city"?"bp":"bg"}`} onClick={()=>setAreaView("city")}>Villes</button>
            </div>
          </div>

          {areaView==="region" && regionList.map(r => {
            const pct = totalAnnual>0 ? Math.round(r.annual/totalAnnual*100) : 0;
            const color = REGION_COLORS[r.name]||"#888";
            const isExpanded = expandedRegion===r.name;
            const cityList = Object.entries(r.cities).map(([c,d])=>({city:c,...d})).sort((a,b)=>b.annual-a.annual);
            return (
              <div key={r.name}>
                <div style={{padding:"10px 0",borderBottom:"1px solid var(--b1)",cursor:"pointer"}} onClick={()=>setExpandedRegion(isExpanded?null:r.name)}>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:5}}>
                    <div style={{display:"flex",alignItems:"center",gap:8}}>
                      <div style={{width:10,height:10,borderRadius:"50%",background:color,flexShrink:0}}/>
                      <span style={{fontSize:13,fontWeight:600,color:"var(--t1)"}}>{r.name}</span>
                      <span style={{fontSize:10,color:"var(--t3)"}}>{r.count} client{r.count>1?"s":""} · {pct}%</span>
                      <span style={{fontSize:10,color:"var(--t3)"}}>{isExpanded?"▲":"▼"}</span>
                    </div>
                    <div style={{fontFamily:"'Outfit',sans-serif",fontSize:15,fontWeight:700,color}}>{fmt(r.annual)}</div>
                  </div>
                  <div className="pbar" style={{height:5}}>
                    <div style={{height:"100%",borderRadius:2,background:color,width:`${pct}%`,transition:"width .3s"}}/>
                  </div>
                </div>
                {isExpanded && (
                  <div style={{padding:"6px 0 6px 24px",borderBottom:"1px solid var(--b1)"}}>
                    {cityList.map(ct=>(
                      <div key={ct.city} style={{display:"flex",justifyContent:"space-between",padding:"4px 0",fontSize:12}}>
                        <span style={{color:"var(--t2)"}}>{ct.city} <span style={{color:"var(--t3)",fontSize:10}}>({ct.count})</span></span>
                        <span style={{color,fontWeight:600}}>{fmt(ct.annual)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}

          {areaView==="city" && areaList.slice(0,20).map(a => {
            const pct = totalAnnual>0 ? Math.round(a.annual/totalAnnual*100) : 0;
            const color = REGION_COLORS[a.region]||"#888";
            return (
              <div key={a.city} style={{padding:"9px 0",borderBottom:"1px solid var(--b1)"}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:4}}>
                  <div>
                    <span style={{fontSize:12,fontWeight:600,color:"var(--t1)"}}>{a.city}</span>
                    <span style={{fontSize:10,color:"var(--t3)",marginLeft:8}}>{a.count} client{a.count>1?"s":""}</span>
                    <span style={{fontSize:9,color,marginLeft:6,padding:"1px 5px",borderRadius:3,background:color+"22"}}>{a.region}</span>
                  </div>
                  <div style={{fontFamily:"'Outfit',sans-serif",fontSize:13,fontWeight:700,color:"var(--pri)"}}>{fmt(a.annual)}</div>
                </div>
                <div className="pbar" style={{height:4}}>
                  <div className="pfill" style={{width:`${pct}%`}}/>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {recurring.length>0 && (
        <div className="card" style={{marginTop:14}}>
          <div className="sh"><div className="st">Clients récurrents — détail</div></div>
          <div className="twrap"><table><thead><tr><th>Nom</th><th>Adresse</th><th>Par visite</th><th>Fréquence</th><th>Revenu annuel</th></tr></thead>
          <tbody>{[...recurring].sort((a,b)=>b.annualIn-a.annualIn).map(c=>(
            <tr key={c.id}>
              <td className="tdn">{c.fullName}</td>
              <td>{c.company}</td>
              <td style={{color:"var(--pri)",fontWeight:600}}>{fmt(c.prix)}</td>
              <td><span className="tag ta">{getFreqLabel(c.freq)}</span></td>
              <td style={{color:"var(--pri)",fontWeight:700,fontFamily:"'Outfit',sans-serif"}}>{fmt(c.annualIn)}</td>
            </tr>
          ))}</tbody></table></div>
        </div>
      )}
    </>
  );
}

// ── Price Calculator ────────────────────────────────────────────────────────────
const DEFAULT_SECTIONS = [
  { title:"Standard", items:[
    {code:"pb",name:"Panneau Balcon",price:5},{code:"m",name:"Manivelle",price:7},{code:"mc",name:"Manivelle à carreaux",price:12.75},
    {code:"mi",name:"Manivelle à imposte",price:9.5},{code:"i",name:"Imposte",price:4.5},{code:"ic",name:"Imposte à carreaux",price:6.5},
    {code:"m32",name:"Manivelle 32'",price:10},{code:"mc32",name:"Manivelle à carreaux 32'",price:17.5},{code:"mi32",name:"Manivelle à imposte 32'",price:12.5},
    {code:"eu",name:"Européenne",price:13},{code:"cs",name:"Coulissante simple",price:13},{code:"cd",name:"Coulissante double",price:25},
    {code:"pcd",name:"Petite coulissante double",price:15.5},{code:"ct",name:"Coulissante triple",price:37.5},{code:"ctt",name:"Coulissante thermos triple",price:21.5},
    {code:"gs",name:"Guillotine simple",price:13},{code:"gsc",name:"Guillotine carreau/standard",price:19.5},{code:"gd",name:"Guillotine double",price:24.5},
    {code:"gc",name:"Guillotine à carreaux",price:31.5},{code:"pps",name:"Porte patio simple",price:21.5},{code:"ppd",name:"Porte patio double",price:31.5},
    {code:"pps2",name:"Porte patio simple 2 panneaux",price:25.5},{code:"ppt",name:"Porte patio triple",price:44.5},{code:"pptt",name:"Porte patio thermos triple",price:26.5},
    {code:"ps",name:"Porte standard",price:6.5},{code:"pc",name:"Porte à carreaux",price:12.5},
    {code:"fx",name:"Fixe",price:9},{code:"pfx",name:"Petite Fixe",price:7},{code:"mfx",name:"Moyenne Fixe",price:11},{code:"gfx",name:"Grand Fixe",price:15},
    {code:"pl",name:"Puit de lumière",price:19.5},{code:"gi",name:"Grand imposte",price:10.5},
    {code:"m2",name:"Manivelle 2 panneaux",price:8.5},{code:"m3",name:"Manivelle 3 panneaux",price:10.5},{code:"m4",name:"Manivelle 4 panneaux",price:11.5},
  ]},
  { title:"2e étage (SS)", items:[
    {code:"mss",name:"Manivelles",price:7.5},{code:"csss",name:"Coulissantes simple",price:12.5},{code:"cdss",name:"Coulissantes double",price:19.5},
    {code:"pcdss",name:"Petites coulissantes double",price:14.5},{code:"ctss",name:"Coulissantes triple",price:39.5},{code:"cttss",name:"Coulissante thermos triple",price:21.5},
    {code:"fxss",name:"Fixe",price:19.5},{code:"bdss",name:"À bascule double",price:12.5},{code:"psss",name:"Porte standard",price:6.5},
    {code:"ppsss",name:"Porte patio simple",price:21.5},{code:"gdss",name:"Guillotine double",price:24.5},
  ]},
  { title:"Véranda (V)", items:[
    {code:"cv",name:"Panneau côté",price:9.5},{code:"tv",name:"Panneau toit",price:10.5},{code:"mv",name:"Manivelle",price:6.5},
    {code:"csv",name:"Coulissante simple",price:12.5},{code:"cdv",name:"Coulissante double",price:19.5},
    {code:"ppsv",name:"Porte patio simple",price:21.5},{code:"ppdv",name:"Porte patio double",price:31.5},
    {code:"fxv",name:"Fixe",price:9.5},{code:"pfxv",name:"Petite fixe",price:6.5},{code:"mfxv",name:"Moyenne fixe",price:10.5},
    {code:"gfxv",name:"Grand fixe",price:16.5},{code:"psv",name:"Porte standard",price:6.5},
  ]},
];

function PriceCalculator() {
  const [quantities,setQuantities] = useState({});
  const [customPrices,setCustomPrices] = useState({});
  const [editMode,setEditMode] = useState(false);
  const [saved,setSaved] = useState(false);

  // Load saved prices on mount
  useEffect(()=>{
    try {
      const stored = localStorage.getItem("ll_custom_prices");
      if(stored) setCustomPrices(JSON.parse(stored));
    } catch {}
  },[]);

  const getPrice = (code,defaultPrice) => customPrices[code] !== undefined ? customPrices[code] : defaultPrice;
  const setPrice = (code,val) => setCustomPrices(p=>({...p,[code]:parseFloat(val)||0}));
  const setQty = (code,val) => setQuantities(q=>({...q,[code]:parseInt(val)||0}));

  const savePrices = () => {
    localStorage.setItem("ll_custom_prices", JSON.stringify(customPrices));
    setSaved(true);
    setTimeout(()=>setSaved(false),2500);
  };

  const resetPrices = () => {
    setCustomPrices({});
    localStorage.removeItem("ll_custom_prices");
    setSaved(true);
    setTimeout(()=>setSaved(false),2500);
  };

  let totalIn = 0, totalOut = 0, totalWindows = 0;
  DEFAULT_SECTIONS.forEach(sec=>sec.items.forEach(it=>{
    const q = quantities[it.code]||0;
    const p = getPrice(it.code, it.price);
    totalIn += p * q;
    totalOut += (p/2) * q;
    totalWindows += q;
  }));

  const resetQty = () => setQuantities({});
  const modifiedCount = Object.keys(customPrices).length;

  return (
    <>
      <div className="kgrid" style={{gridTemplateColumns:"repeat(3,1fr)"}}>
        <div className="kcard">
          <div className="klbl">Total In/Out</div>
          <div className="kval blu">${totalIn.toFixed(2)}</div>
          <div className="ksub">{totalWindows} fenêtres</div>
        </div>
        <div className="kcard">
          <div className="klbl">Out seulement</div>
          <div className="kval">${totalOut.toFixed(2)}</div>
          <div className="ksub">~50% du prix complet</div>
        </div>
        <div className="kcard" style={{display:"flex",flexDirection:"column",justifyContent:"center",alignItems:"center",gap:6}}>
          <button className="btn bp" onClick={resetQty}>Réinitialiser</button>
          <button className={`btn ${editMode?"bp":"bg"} bsm`} onClick={()=>setEditMode(!editMode)}>
            {editMode?"✓ Fin de modification":"✎ Modifier les prix"}
          </button>
        </div>
      </div>

      {editMode && (
        <div className="card" style={{marginBottom:14,display:"flex",alignItems:"center",justifyContent:"space-between",flexWrap:"wrap",gap:8}}>
          <div style={{fontSize:12,color:"var(--t2)"}}>
            Mode édition — cliquez sur un prix pour le modifier.
            {modifiedCount>0 && <span style={{color:"var(--pri)",marginLeft:8}}>{modifiedCount} prix modifié{modifiedCount>1?"s":""}</span>}
          </div>
          <div style={{display:"flex",gap:6}}>
            <button className="btn bp" onClick={savePrices}>💾 Sauvegarder les prix</button>
            <button className="btn bd bsm" onClick={resetPrices}>Réinitialiser aux défauts</button>
          </div>
          {saved && <div style={{fontSize:11,color:"var(--grn)",width:"100%"}}>✓ Prix sauvegardés!</div>}
        </div>
      )}

      {DEFAULT_SECTIONS.map(sec => {
        const secTotal = sec.items.reduce((s,it)=>(getPrice(it.code,it.price)*(quantities[it.code]||0))+s,0);
        return (
          <div key={sec.title} className="card" style={{marginBottom:14}}>
            <div className="sh">
              <div className="st">{sec.title}</div>
              {secTotal>0 && <div style={{fontFamily:"'Outfit',sans-serif",fontSize:15,fontWeight:700,color:"var(--pri)"}}>${secTotal.toFixed(2)}</div>}
            </div>
            <div className="twrap"><table><thead><tr>
              <th style={{width:"40%"}}>Type de fenêtre</th><th>Code</th><th>{editMode?"Prix (éditable)":"Prix unitaire"}</th><th style={{width:80}}>Quantité</th><th>Sous-total</th>
            </tr></thead>
            <tbody>{sec.items.map(it=>{
              const q = quantities[it.code]||0;
              const p = getPrice(it.code, it.price);
              const sub = p*q;
              const isModified = customPrices[it.code] !== undefined && customPrices[it.code] !== it.price;
              return (
                <tr key={it.code} style={{background:q>0?"var(--pri-a)":"transparent"}}>
                  <td className="tdn">{it.name}</td>
                  <td style={{color:"var(--t3)",fontSize:10}}>{it.code}</td>
                  <td>{editMode ? (
                    <div style={{display:"flex",alignItems:"center",gap:4}}>
                      <span style={{color:"var(--t3)"}}>$</span>
                      <input type="number" step="0.25" min="0" className="fi" style={{width:70,padding:"4px 6px",textAlign:"center",borderColor:isModified?"var(--pri)":"var(--b1)"}} value={p} onChange={e=>setPrice(it.code,e.target.value)} />
                      {isModified && <span style={{fontSize:9,color:"var(--t3)",textDecoration:"line-through"}}>${it.price.toFixed(2)}</span>}
                    </div>
                  ) : (
                    <span style={{color:isModified?"var(--pri)":"var(--t2)"}}>${p.toFixed(2)}{isModified?" *":""}</span>
                  )}</td>
                  <td><input type="number" min="0" className="fi" style={{width:60,padding:"4px 6px",textAlign:"center"}} value={q||""} onChange={e=>setQty(it.code,e.target.value)} placeholder="0"/></td>
                  <td style={{fontWeight:sub>0?700:400,color:sub>0?"var(--pri)":"var(--t3)"}}>{sub>0?`$${sub.toFixed(2)}`:""}</td>
                </tr>
              );
            })}</tbody></table></div>
          </div>
        );
      })}
    </>
  );
}

// ── Tax Calculator (TPS + TVQ Québec) ──────────────────────────────────────────
// Rates hardcoded — works fully offline. Stable since 2013.
// TPS 5%, TVQ 9.975%, both applied on pre-tax amount (not compounded).
function TaxCalculator() {
  const [amount,setAmount] = useState("");
  const [mode,setMode] = useState("forward"); // forward = HT→TTC, reverse = TTC→HT

  const TPS_RATE = 0.05;
  const TVQ_RATE = 0.09975;
  const COMBINED = 1.14975;

  const amt = parseFloat(amount) || 0;
  let ht, tps, tvq, ttc;
  if (mode === "forward") {
    ht  = amt;
    tps = amt * TPS_RATE;
    tvq = amt * TVQ_RATE;
    ttc = amt * COMBINED;
  } else {
    ht  = amt / COMBINED;
    tps = ht * TPS_RATE;
    tvq = ht * TVQ_RATE;
    ttc = amt;
  }

  return (
    <>
      <div className="card" style={{marginBottom:14}}>
        <div className="sh">
          <div className="st">Calculateur de taxes — TPS + TVQ Québec</div>
        </div>
        <div style={{display:"flex",gap:6,marginBottom:14,flexWrap:"wrap"}}>
          <button className={`btn ${mode==="forward"?"bp":"bg"} bsm`} onClick={()=>setMode("forward")}>
            Avant taxes → Avec taxes
          </button>
          <button className={`btn ${mode==="reverse"?"bp":"bg"} bsm`} onClick={()=>setMode("reverse")}>
            Avec taxes → Avant taxes
          </button>
        </div>
        <div style={{display:"flex",alignItems:"center",gap:10,flexWrap:"wrap"}}>
          <label style={{fontSize:13,color:"var(--t2)",minWidth:180}}>
            {mode==="forward" ? "Montant avant taxes" : "Montant avec taxes"}
          </label>
          <div style={{display:"flex",alignItems:"center",gap:4,flex:1,maxWidth:260}}>
            <span style={{color:"var(--t3)",fontSize:18}}>$</span>
            <input
              type="number"
              step="0.01"
              min="0"
              inputMode="decimal"
              className="fi"
              style={{flex:1,padding:"8px 10px",fontSize:16}}
              value={amount}
              onChange={e=>setAmount(e.target.value)}
              placeholder="0.00"
              autoFocus
            />
          </div>
          <button className="btn bg bsm" onClick={()=>setAmount("")}>Effacer</button>
        </div>
      </div>

      <div className="kgrid" style={{gridTemplateColumns:"repeat(4,1fr)"}}>
        <div className="kcard">
          <div className="klbl">Montant HT</div>
          <div className="kval">${ht.toFixed(2)}</div>
          <div className="ksub">Avant taxes</div>
        </div>
        <div className="kcard">
          <div className="klbl">TPS (5%)</div>
          <div className="kval">${tps.toFixed(2)}</div>
          <div className="ksub">Fédéral</div>
        </div>
        <div className="kcard">
          <div className="klbl">TVQ (9.975%)</div>
          <div className="kval">${tvq.toFixed(2)}</div>
          <div className="ksub">Provincial</div>
        </div>
        <div className="kcard">
          <div className="klbl">Total TTC</div>
          <div className="kval blu">${ttc.toFixed(2)}</div>
          <div className="ksub">Avec taxes (14.975%)</div>
        </div>
      </div>

      <div className="card" style={{marginTop:14}}>
        <div className="sh">
          <div className="st">Référence</div>
        </div>
        <div style={{fontSize:12,color:"var(--t2)",lineHeight:1.7}}>
          • TPS : 5 % &nbsp;·&nbsp; TVQ : 9,975 % &nbsp;·&nbsp; Combiné : 14,975 %<br/>
          • Formule directe : <strong>TTC = HT × 1,14975</strong><br/>
          • Formule inverse : <strong>HT = TTC ÷ 1,14975</strong><br/>
          • Les deux taxes s'appliquent sur le montant avant taxes (depuis 2013, non cumulatif).
        </div>
      </div>
    </>
  );
}

// ── Pipeline ───────────────────────────────────────────────────────────────────
function Pipeline({deals,onEdit,onMove,onDelete,cName}) {
  const [expanded,setExpanded] = useState(null);
  return (
    <div className="kanban">{STAGES.map(stage => {
      const sd = deals.filter(d=>d.stage===stage);
      const sv = sd.reduce((s,d)=>s+d.value,0);
      return (
        <div key={stage} className="kcol">
          <div className="kch"><div className="kct"><div className="sdot" style={{background:STAGE_COLORS[stage]}}/>{stage}</div><span className="kcc">{sd.length}</span></div>
          {sv>0 && <div className="kctot"><strong>{fmt(sv)}</strong></div>}
          {sd.map(deal => (
            <div key={deal.id} className="dc" onClick={()=>setExpanded(expanded===deal.id?null:deal.id)}>
              <div className="dt-title">{deal.title}</div>
              <div className="dco">{deal.contactName||cName(deal.contactId)}</div>
              <div className="dv">{fmt(deal.value)}</div>
              <div className="dm"><span>{deal.probability}%</span><span>{fmtDate(deal.closeDate)}</span></div>
              <div className="pbar"><div className="pfill" style={{width:`${deal.probability}%`}}/></div>
              {expanded===deal.id && (
                <div className="dactions" onClick={e=>e.stopPropagation()}>
                  {STAGES.filter(s=>s!==stage).map(s => <button key={s} className="btn bg bsm" style={{fontSize:9}} onClick={()=>{onMove(deal.id,s);setExpanded(null);}}>→ {s}</button>)}
                  <button className="btn bg bsm" style={{fontSize:9}} onClick={()=>onEdit(deal)}>Edit</button>
                  <button className="btn bd bsm" style={{fontSize:9}} onClick={()=>{onDelete(deal.id);setExpanded(null);}}>Del</button>
                </div>
              )}
            </div>
          ))}
          {sd.length===0 && <div className="empty-col">Empty</div>}
        </div>
      );
    })}</div>
  );
}

// ── Tasks ──────────────────────────────────────────────────────────────────────
function Tasks({tasks,onToggle,onEdit,onDelete,cName}) {
  const pending = tasks.filter(t=>!t.completed);
  const done = tasks.filter(t=>t.completed);
  const Item = ({t}) => (
    <div className="ti">
      <div className={`tck ${t.completed?"dn":""}`} onClick={()=>onToggle(t.id)}>
        {t.completed && <span style={{color:"#07090f",fontSize:10,fontWeight:700}}>✓</span>}
      </div>
      <div style={{flex:1}}>
        <div className={`ttl ${t.completed?"dt-dn":""}`}>{t.title}</div>
        <div className="tmt">
          <span style={{color:isOverdue(t.dueDate)&&!t.completed?"#f87171":""}}>
            {isOverdue(t.dueDate)&&!t.completed?"⚠ ":""}Due {fmtDate(t.dueDate)}
          </span>
          {(t.contactName||t.contactId) && <span>· {t.contactName||cName(t.contactId)}</span>}
          <span style={{color:PRIORITY_COLORS[t.priority]}}>· {t.priority}</span>
        </div>
      </div>
      <div className="ra">
        <button className="btn bg bsm" onClick={()=>onEdit(t)}>Edit</button>
        <button className="btn bd bsm" onClick={()=>onDelete(t.id)}>Del</button>
      </div>
    </div>
  );
  return (
    <div>
      <div className="card" style={{marginBottom:14}}>
        <div className="sh" style={{marginBottom:0}}><div className="st">Pending ({pending.length})</div></div>
        {pending.length===0 && <div className="empty"><div className="eico">✓</div>All tasks complete!</div>}
        {pending.map(t => <Item key={t.id} t={t}/>)}
      </div>
      {done.length>0 && <div className="card"><div className="st" style={{marginBottom:11,opacity:.6}}>Completed ({done.length})</div>{done.map(t => <Item key={t.id} t={t}/>)}</div>}
    </div>
  );
}

// ── Calendar (Scheduling) ────────────────────────────────────────────────────────
const TEAM_COLORS = { A:"#60a5fa", B:"#4ade80", C:"#fbbf24", D:"#fb923c", E:"#f87171", F:"#a78bfa" };
const teamColor = t => TEAM_COLORS[t] || "#94a3b8";
const DAY_NAMES = ["Dim","Lun","Mar","Mer","Jeu","Ven","Sam"];
const MONTH_NAMES = ["Janvier","Février","Mars","Avril","Mai","Juin","Juillet","Août","Septembre","Octobre","Novembre","Décembre"];

const toISO = d => {
  const y = d.getFullYear(), m = String(d.getMonth()+1).padStart(2,"0"), day = String(d.getDate()).padStart(2,"0");
  return `${y}-${m}-${day}`;
};
const parseISO = s => new Date(s+"T12:00:00");
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate()+n); return x; };
const startOfWeek = d => addDays(d, -((d.getDay()+7)%7)); // week starts Sunday

function Calendar({ jobs, contacts, onCreate, onUpdate, onMove, onDelete, notify }) {
  const [mode, setMode] = useState("day"); // day | week | month
  const [cursor, setCursor] = useState(() => new Date());
  const [teams, setTeams] = useState(() => {
    try { const s = localStorage.getItem("ll_teams"); if (s) return JSON.parse(s); } catch {}
    return ["A","B","C"];
  });
  const [jobModal, setJobModal] = useState(null); // {date, team, job?}
  const [pdfModal, setPdfModal] = useState(null);  // {date, team}
  const [dragId, setDragId] = useState(null);

  const saveTeams = (t) => { setTeams(t); try { localStorage.setItem("ll_teams", JSON.stringify(t)); } catch {} };
  const addTeam = () => {
    const next = String.fromCharCode(65 + teams.length); // A,B,C...
    if (teams.length >= 6) { notify("Maximum 6 équipes", "err"); return; }
    saveTeams([...teams, next]);
  };
  const removeTeam = (t) => {
    if (teams.length <= 1) return;
    const has = jobs.some(j => j.team === t);
    if (has && !window.confirm(`L'équipe ${t} a des jobs. Les retirer de la vue (les jobs ne sont pas supprimés) ?`)) return;
    saveTeams(teams.filter(x => x !== t));
  };

  const jobsByDate = (iso) => jobs.filter(j => j.jobDate && j.jobDate.slice(0,10) === iso);
  const jobsFor = (iso, team) => jobsByDate(iso).filter(j => j.team === team).sort((a,b)=>a.position-b.position);

  // ── Drag-and-drop ──
  const onDropTo = (iso, team, beforeId=null) => {
    if (dragId == null) return;
    const job = jobs.find(j => j.id === dragId);
    if (!job) { setDragId(null); return; }
    const target = jobsFor(iso, team).filter(j => j.id !== dragId);
    let position = target.length;
    if (beforeId != null) {
      const i = target.findIndex(j => j.id === beforeId);
      if (i >= 0) position = i;
    }
    onMove(job.id, { team, jobDate: iso, position });
    setDragId(null);
  };

  // ── Navigation ──
  const nav = (dir) => {
    if (mode === "day") setCursor(addDays(cursor, dir));
    else if (mode === "week") setCursor(addDays(cursor, dir*7));
    else { const x = new Date(cursor); x.setMonth(x.getMonth()+dir); setCursor(x); }
  };
  const today = () => setCursor(new Date());

  const headerLabel = () => {
    if (mode === "day") return parseISO(toISO(cursor)).toLocaleDateString("fr-CA",{weekday:"long",day:"numeric",month:"long",year:"numeric"});
    if (mode === "week") { const s = startOfWeek(cursor), e = addDays(s,6); return `${s.getDate()} ${MONTH_NAMES[s.getMonth()].slice(0,3)} – ${e.getDate()} ${MONTH_NAMES[e.getMonth()].slice(0,3)} ${e.getFullYear()}`; }
    return `${MONTH_NAMES[cursor.getMonth()]} ${cursor.getFullYear()}`;
  };

  // ── Job card ──
  const JobCard = ({ job, compact }) => (
    <div
      draggable
      onDragStart={()=>setDragId(job.id)}
      onDragEnd={()=>setDragId(null)}
      onDragOver={e=>e.preventDefault()}
      onDrop={e=>{e.stopPropagation(); onDropTo(job.jobDate.slice(0,10), job.team, job.id);}}
      onClick={()=>setJobModal({ date: job.jobDate.slice(0,10), team: job.team, job })}
      className="card"
      style={{padding:compact?"5px 7px":"8px 10px",marginBottom:6,cursor:"pointer",borderLeft:`3px solid ${teamColor(job.team)}`}}
      title="Cliquer pour modifier · glisser pour déplacer"
    >
      <div style={{display:"flex",justifyContent:"space-between",gap:6,alignItems:"baseline"}}>
        <span style={{fontWeight:600,fontSize:compact?12:13,color:"var(--t1)",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{job.clientName}</span>
        {job.timeSlot && <span style={{fontSize:11,color:"var(--t3)",whiteSpace:"nowrap"}}>{job.timeSlot}</span>}
      </div>
      {!compact && job.service && <div style={{fontSize:11,color:"var(--t2)"}}>{job.service}</div>}
      {!compact && job.address && <div style={{fontSize:11,color:"var(--t3)",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{job.address}</div>}
      {job.isExternal && <span style={{fontSize:10,color:"#fb923c"}}>⚠ Pas dans le CRM</span>}
    </div>
  );

  // ── DAY VIEW ──
  const DayView = () => {
    const iso = toISO(cursor);
    return (
      <div style={{display:"flex",gap:10,overflowX:"auto",paddingBottom:8}}>
        {teams.map(team => (
          <div key={team}
            onDragOver={e=>e.preventDefault()}
            onDrop={()=>onDropTo(iso, team)}
            style={{flex:"1 0 220px",minWidth:220,background:"var(--bg2,rgba(0,0,0,.02))",borderRadius:10,padding:8}}
          >
            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:8}}>
              <div style={{display:"flex",alignItems:"center",gap:6}}>
                <span style={{width:10,height:10,borderRadius:3,background:teamColor(team),display:"inline-block"}}></span>
                <strong style={{fontSize:14}}>Équipe {team}</strong>
                <span style={{fontSize:12,color:"var(--t3)"}}>({jobsFor(iso,team).length})</span>
              </div>
              <div style={{display:"flex",gap:4}}>
                <button className="btn bg bsm" title="Imprimer / PDF" onClick={()=>setPdfModal({date:iso, team})}>🖨</button>
                {teams.length>1 && <button className="btn bg bsm" title="Retirer l'équipe de la vue" onClick={()=>removeTeam(team)}>✕</button>}
              </div>
            </div>
            {jobsFor(iso,team).map(j => <JobCard key={j.id} job={j} />)}
            <button className="btn bg bsm" style={{width:"100%",marginTop:4}} onClick={()=>setJobModal({date:iso, team})}>+ Ajouter</button>
          </div>
        ))}
        <div style={{flex:"0 0 auto",alignSelf:"flex-start"}}>
          <button className="btn bg bsm" onClick={addTeam}>+ Équipe</button>
        </div>
      </div>
    );
  };

  // ── WEEK VIEW ──
  const WeekView = () => {
    const s = startOfWeek(cursor);
    const days = Array.from({length:7}, (_,i)=>addDays(s,i));
    return (
      <div style={{display:"grid",gridTemplateColumns:"repeat(7,1fr)",gap:6}}>
        {days.map(d => {
          const iso = toISO(d);
          const isToday = iso === toISO(new Date());
          return (
            <div key={iso}
              onDragOver={e=>e.preventDefault()}
              onDrop={()=>{ const job = jobs.find(j=>j.id===dragId); if(job) onDropTo(iso, job.team); }}
              style={{minHeight:120,background:"var(--bg2,rgba(0,0,0,.02))",borderRadius:8,padding:6,border:isToday?`1px solid ${teamColor("A")}`:"1px solid transparent"}}
            >
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:4}}>
                <span style={{fontSize:12,fontWeight:600,color:isToday?teamColor("A"):"var(--t2)"}}>{DAY_NAMES[d.getDay()]} {d.getDate()}</span>
                <button className="btn bg bsm" style={{padding:"0 6px"}} onClick={()=>setJobModal({date:iso, team:teams[0]})}>+</button>
              </div>
              {jobsByDate(iso).sort((a,b)=> a.team.localeCompare(b.team) || a.position-b.position).map(j => <JobCard key={j.id} job={j} compact />)}
            </div>
          );
        })}
      </div>
    );
  };

  // ── MONTH VIEW ──
  const MonthView = () => {
    const first = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
    const gridStart = startOfWeek(first);
    const cells = Array.from({length:42}, (_,i)=>addDays(gridStart,i));
    return (
      <div>
        <div style={{display:"grid",gridTemplateColumns:"repeat(7,1fr)",gap:4,marginBottom:4}}>
          {DAY_NAMES.map(n => <div key={n} style={{textAlign:"center",fontSize:11,color:"var(--t3)",fontWeight:600}}>{n}</div>)}
        </div>
        <div style={{display:"grid",gridTemplateColumns:"repeat(7,1fr)",gap:4}}>
          {cells.map(d => {
            const iso = toISO(d);
            const inMonth = d.getMonth() === cursor.getMonth();
            const isToday = iso === toISO(new Date());
            const dayJobs = jobsByDate(iso);
            return (
              <div key={iso}
                onClick={()=>{ setCursor(d); setMode("day"); }}
                style={{minHeight:84,background:inMonth?"var(--bg2,rgba(0,0,0,.02))":"transparent",borderRadius:7,padding:5,cursor:"pointer",opacity:inMonth?1:.4,border:isToday?`1px solid ${teamColor("A")}`:"1px solid transparent"}}
                title="Cliquer pour voir la journée"
              >
                <div style={{fontSize:12,fontWeight:isToday?700:500,color:isToday?teamColor("A"):"var(--t2)",marginBottom:3}}>{d.getDate()}</div>
                {dayJobs.slice(0,3).map(j => (
                  <div key={j.id} style={{fontSize:10,color:"var(--t1)",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",borderLeft:`2px solid ${teamColor(j.team)}`,paddingLeft:4,marginBottom:2}}>
                    {j.timeSlot?`${j.timeSlot} `:""}{j.clientName}
                  </div>
                ))}
                {dayJobs.length>3 && <div style={{fontSize:10,color:"var(--t3)"}}>+{dayJobs.length-3} de plus</div>}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div>
      <div className="card" style={{marginBottom:14}}>
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:8,flexWrap:"wrap"}}>
          <div style={{display:"flex",gap:6}}>
            {["day","week","month"].map(m => (
              <button key={m} className={`btn ${mode===m?"bp":"bg"} bsm`} onClick={()=>setMode(m)}>
                {m==="day"?"Jour":m==="week"?"Semaine":"Mois"}
              </button>
            ))}
          </div>
          <div style={{display:"flex",alignItems:"center",gap:8}}>
            <button className="btn bg bsm" onClick={()=>nav(-1)}>‹</button>
            <button className="btn bg bsm" onClick={today}>Aujourd'hui</button>
            <button className="btn bg bsm" onClick={()=>nav(1)}>›</button>
            <strong style={{fontSize:14,minWidth:160,textAlign:"center",textTransform:"capitalize"}}>{headerLabel()}</strong>
          </div>
        </div>
      </div>

      {mode==="day" && <DayView />}
      {mode==="week" && <WeekView />}
      {mode==="month" && <MonthView />}

      {jobModal && <JobModal info={jobModal} teams={teams} contacts={contacts}
        onClose={()=>setJobModal(null)}
        onCreate={onCreate} onUpdate={onUpdate} onDelete={onDelete} />}
      {pdfModal && <RouteSheetModal info={pdfModal} jobs={jobsFor(pdfModal.date, pdfModal.team)}
        onClose={()=>setPdfModal(null)} />}
    </div>
  );
}

// ── Job add/edit modal with client autocomplete ──
function JobModal({ info, teams, contacts, onClose, onCreate, onUpdate, onDelete }) {
  const editing = !!info.job;
  const j = info.job || {};
  const [clientName, setClientName] = useState(j.clientName || "");
  const [address, setAddress] = useState(j.address || "");
  const [phone, setPhone] = useState(j.phone || "");
  const [service, setService] = useState(j.service || "");
  const [timeSlot, setTimeSlot] = useState(j.timeSlot || "");
  const [notes, setNotes] = useState(j.notes || "");
  const [team, setTeam] = useState(j.team || info.team || teams[0]);
  const [contactId, setContactId] = useState(j.contactId || null);
  const [isExternal, setIsExternal] = useState(editing ? !!j.isExternal : true);
  const [sugOpen, setSugOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  // Autocomplete against active contacts (already loaded, decrypted)
  const matches = clientName.trim().length >= 2
    ? contacts.filter(c => c.status !== "Archived" &&
        (c.fullName||"").toLowerCase().includes(clientName.trim().toLowerCase())).slice(0,6)
    : [];

  const pickContact = (c) => {
    setClientName(c.fullName || "");
    setAddress(c.company || "");
    setPhone(c.phone || "");
    setContactId(c.id);
    setIsExternal(false);
    setSugOpen(false);
  };

  const save = async () => {
    if (!clientName.trim()) return;
    setBusy(true);
    const payload = {
      jobDate: info.date, team, contactId: contactId || null,
      clientName: clientName.trim(), address: address || null, phone: phone || null,
      service: service || null, timeSlot: timeSlot || null, notes: notes || null,
      isExternal: contactId ? false : isExternal,
    };
    try {
      if (editing) await onUpdate(j.id, payload);
      else await onCreate(payload);
      onClose();
    } catch { setBusy(false); }
  };

  const del = async () => {
    if (!window.confirm("Supprimer ce job ?")) return;
    setBusy(true);
    try { await onDelete(j.id); onClose(); } catch { setBusy(false); }
  };

  return (
    <div className="ov" onClick={onClose}>
      <div className="mo" onClick={e=>e.stopPropagation()} style={{maxWidth:460}}>
        <div className="mtl">{editing?"Modifier le job":"Nouveau job"} — {parseISO(info.date).toLocaleDateString("fr-CA",{weekday:"short",day:"numeric",month:"short"})}</div>

        <label className="fl">Client</label>
        <div style={{position:"relative"}}>
          <input className="fi" value={clientName} autoFocus
            onChange={e=>{setClientName(e.target.value); setSugOpen(true); setContactId(null); setIsExternal(true);}}
            onFocus={()=>setSugOpen(true)}
            placeholder="Taper quelques lettres…" />
          {sugOpen && matches.length>0 && (
            <div className="card" style={{position:"absolute",top:"100%",left:0,right:0,zIndex:20,maxHeight:200,overflowY:"auto",padding:4,marginTop:2}}>
              {matches.map(c => (
                <div key={c.id} onClick={()=>pickContact(c)} style={{padding:"6px 8px",cursor:"pointer",borderRadius:6,fontSize:13}}
                  onMouseEnter={e=>e.currentTarget.style.background="var(--bg2,rgba(0,0,0,.04))"}
                  onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                  <div style={{fontWeight:600,color:"var(--t1)"}}>{c.fullName}</div>
                  {c.company && <div style={{fontSize:11,color:"var(--t3)"}}>{c.company}</div>}
                </div>
              ))}
            </div>
          )}
        </div>
        {isExternal && !contactId && clientName.trim() && <div style={{fontSize:11,color:"#fb923c",marginTop:2}}>⚠ Client hors CRM (one-off)</div>}

        <div style={{display:"flex",gap:8}}>
          <div style={{flex:1}}>
            <label className="fl">Adresse</label>
            <input className="fi" value={address} onChange={e=>setAddress(e.target.value)} />
          </div>
          <div style={{flex:1}}>
            <label className="fl">Téléphone</label>
            <input className="fi" value={phone} onChange={e=>setPhone(e.target.value)} />
          </div>
        </div>

        <div style={{display:"flex",gap:8}}>
          <div style={{flex:1}}>
            <label className="fl">Service</label>
            <input className="fi" value={service} onChange={e=>setService(e.target.value)} placeholder="Fenêtres, gouttières…" />
          </div>
          <div style={{flex:1}}>
            <label className="fl">Plage horaire</label>
            <input className="fi" value={timeSlot} onChange={e=>setTimeSlot(e.target.value)} placeholder="ex. 9h-11h" />
          </div>
        </div>

        <label className="fl">Équipe</label>
        <select className="fi" value={team} onChange={e=>setTeam(e.target.value)}>
          {teams.map(t => <option key={t} value={t}>Équipe {t}</option>)}
        </select>

        <label className="fl">Notes</label>
        <textarea className="fi" rows={2} value={notes} onChange={e=>setNotes(e.target.value)} />

        <div style={{display:"flex",justifyContent:"space-between",marginTop:14}}>
          {editing ? <button className="btn bg bsm" onClick={del} disabled={busy} style={{color:"#f87171"}}>Supprimer</button> : <span/>}
          <div style={{display:"flex",gap:8}}>
            <button className="btn bg" onClick={onClose} disabled={busy}>Annuler</button>
            <button className="btn bp" onClick={save} disabled={busy||!clientName.trim()}>{editing?"Enregistrer":"Ajouter"}</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Route sheet (printable PDF) with field toggles ──
function RouteSheetModal({ info, jobs, onClose }) {
  const [fields, setFields] = useState({ address:true, phone:true, service:true, timeSlot:true, notes:false });
  const toggle = k => setFields(f => ({...f, [k]:!f[k]}));
  const dateLabel = parseISO(info.date).toLocaleDateString("fr-CA",{weekday:"long",day:"numeric",month:"long",year:"numeric"});

  const print = () => {
    const rows = jobs.map((j,i) => {
      const parts = [];
      if (fields.timeSlot && j.timeSlot) parts.push(`<span class="ts">${j.timeSlot}</span>`);
      let line = `<div class="job"><div class="cn">${i+1}. ${esc(j.clientName)}${j.isExternal?' <span class="ext">(hors CRM)</span>':''}</div>`;
      if (parts.length) line += `<div class="meta">${parts.join("")}</div>`;
      if (fields.service && j.service) line += `<div class="row"><b>Service:</b> ${esc(j.service)}</div>`;
      if (fields.address && j.address) line += `<div class="row"><b>Adresse:</b> ${esc(j.address)}</div>`;
      if (fields.phone && j.phone) line += `<div class="row"><b>Tél:</b> ${esc(j.phone)}</div>`;
      if (fields.notes && j.notes) line += `<div class="row"><b>Notes:</b> ${esc(j.notes)}</div>`;
      line += `</div>`;
      return line;
    }).join("");
    const html = `<!doctype html><html><head><meta charset="utf-8"><title>Route Équipe ${info.team} — ${info.date}</title>
      <style>
        body{font-family:-apple-system,Segoe UI,Roboto,sans-serif;color:#111;margin:32px;}
        h1{font-size:20px;margin:0 0 2px;} h2{font-size:14px;font-weight:500;color:#555;margin:0 0 16px;}
        .job{border:1px solid #ddd;border-radius:8px;padding:10px 12px;margin-bottom:8px;page-break-inside:avoid;}
        .cn{font-size:15px;font-weight:700;margin-bottom:2px;} .ext{color:#c2410c;font-weight:400;font-size:12px;}
        .meta{margin-bottom:4px;} .ts{display:inline-block;background:#eef;border-radius:4px;padding:1px 7px;font-size:13px;}
        .row{font-size:13px;line-height:1.5;} .row b{color:#444;}
        .empty{color:#888;font-style:italic;}
        @media print{ .noprint{display:none;} body{margin:12mm;} }
      </style></head><body>
      <h1>Route — Équipe ${info.team}</h1>
      <h2 style="text-transform:capitalize">${dateLabel} · ${jobs.length} job(s)</h2>
      ${rows || '<div class="empty">Aucun job pour cette journée.</div>'}
      <script>window.onload=function(){window.print();}</script>
      </body></html>`;
    const w = window.open("", "_blank");
    if (!w) { alert("Autoriser les pop-ups pour imprimer / exporter en PDF."); return; }
    w.document.write(html); w.document.close();
  };

  const FIELD_LABELS = { address:"Adresse", phone:"Téléphone", service:"Service", timeSlot:"Plage horaire", notes:"Notes" };

  return (
    <div className="ov" onClick={onClose}>
      <div className="mo" onClick={e=>e.stopPropagation()} style={{maxWidth:380}}>
        <div className="mtl">Feuille de route — Équipe {info.team}</div>
        <div style={{fontSize:13,color:"var(--t2)",marginBottom:12,textTransform:"capitalize"}}>{dateLabel} · {jobs.length} job(s)</div>
        <div style={{fontSize:12,color:"var(--t3)",marginBottom:6}}>Champs à inclure :</div>
        <div style={{display:"flex",flexDirection:"column",gap:6,marginBottom:14}}>
          {Object.keys(FIELD_LABELS).map(k => (
            <label key={k} style={{display:"flex",alignItems:"center",gap:8,fontSize:13,cursor:"pointer"}}>
              <input type="checkbox" checked={fields[k]} onChange={()=>toggle(k)} />
              {FIELD_LABELS[k]}
            </label>
          ))}
        </div>
        <div style={{fontSize:11,color:"var(--t3)",marginBottom:12}}>
          Le client et le numéro d'ordre sont toujours inclus. La fenêtre d'impression permet d'enregistrer en PDF (Destination → Enregistrer comme PDF).
        </div>
        <div style={{display:"flex",justifyContent:"flex-end",gap:8}}>
          <button className="btn bg" onClick={onClose}>Fermer</button>
          <button className="btn bp" onClick={print} disabled={jobs.length===0}>🖨 Imprimer / PDF</button>
        </div>
      </div>
    </div>
  );
}

function esc(s){ return String(s==null?"":s).replace(/[&<>"]/g, c=>({"&":"&amp;","<":"&lt;",">":"&gt;",'"':"&quot;"}[c])); }

// ── Password Modal ─────────────────────────────────────────────────────────────
function PasswordModal({onClose, notify}) {
  const [current, setCurrent] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    setError("");
    if(!current || !newPw || !confirm) { setError("Tous les champs sont requis"); return; }
    if(newPw.length < 10) { setError("Le nouveau mot de passe doit contenir au moins 10 caractères"); return; }
    if(newPw !== confirm) { setError("Les mots de passe ne correspondent pas"); return; }
    setSubmitting(true);
    try {
      await api.auth.changePassword(current, newPw);
      notify("Mot de passe mis à jour!");
      onClose();
    } catch(err) {
      setError(err.message || "Échec de la mise à jour");
    }
    setSubmitting(false);
  };

  return (
    <div className="ov" onClick={onClose}><div className="mo" onClick={e=>e.stopPropagation()}>
      <div className="mtl">Changer le mot de passe</div>
      <div className="ff">
        <label className="fl">Mot de passe actuel</label>
        <input className="fi" type="password" value={current} onChange={e=>setCurrent(e.target.value)} placeholder="Votre mot de passe actuel" />
      </div>
      <div className="ff">
        <label className="fl">Nouveau mot de passe</label>
        <input className="fi" type="password" value={newPw} onChange={e=>setNewPw(e.target.value)} placeholder="Minimum 10 caractères" />
      </div>
      <div className="ff">
        <label className="fl">Confirmer le nouveau mot de passe</label>
        <input className="fi" type="password" value={confirm} onChange={e=>setConfirm(e.target.value)} placeholder="Répétez le nouveau mot de passe" />
      </div>
      {error && <p style={{color:"var(--red)",fontSize:11,marginBottom:10}}>{error}</p>}
      <div className="ma">
        <button className="btn bg" onClick={onClose}>Annuler</button>
        <button className="btn bp" onClick={handleSubmit} disabled={submitting}>{submitting?"...":"Mettre à jour"}</button>
      </div>
    </div></div>
  );
}

// ── Rate Modal ──────────────────────────────────────────────────────────────────
function RateModal({onClose, notify}) {
  const [rate, setRate] = useState(getHourlyRate());

  const handleSave = () => {
    localStorage.setItem("ll_hourly_rate", rate.toString());
    notify(`Taux horaire mis à jour: $${rate}/h`);
    onClose();
  };

  const examples = [75,150,250,500,750,1000];

  return (
    <div className="ov" onClick={onClose}><div className="mo" onClick={e=>e.stopPropagation()}>
      <div className="mtl">Taux horaire</div>
      <p style={{fontSize:12,color:"var(--t2)",marginBottom:16,lineHeight:1.6}}>
        Ce taux est utilisé pour estimer automatiquement la durée de chaque contrat. Par exemple, à ${rate}/h un contrat de ${rate*2} prendrait environ 2h.
      </p>
      <div className="ff">
        <label className="fl">Taux horaire ($CAD/heure)</label>
        <input className="fi" type="number" min="1" value={rate} onChange={e=>setRate(parseFloat(e.target.value)||0)} style={{fontSize:18,fontWeight:700,textAlign:"center"}} />
      </div>
      <div style={{marginTop:12,marginBottom:16}}>
        <div style={{fontSize:9.5,color:"var(--t3)",textTransform:"uppercase",letterSpacing:1,marginBottom:8}}>Exemples avec ce taux</div>
        <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:6}}>
          {examples.map(p=>(
            <div key={p} style={{background:"var(--s2)",border:"1px solid var(--b1)",borderRadius:"var(--r)",padding:"8px",textAlign:"center"}}>
              <div style={{fontSize:13,fontWeight:600,color:"var(--pri)"}}>${p}</div>
              <div style={{fontSize:11,color:"var(--grn)",marginTop:2}}>⏱ {autoEstimate(p)}</div>
            </div>
          ))}
        </div>
      </div>
      <div className="ma">
        <button className="btn bg" onClick={onClose}>Annuler</button>
        <button className="btn bp" onClick={handleSave}>Sauvegarder</button>
      </div>
    </div></div>
  );
}

// ── Form Modal ─────────────────────────────────────────────────────────────────
function FormModal({type,data,contacts,onClose,onSave}) {
  const defs = {
    contact:{fullName:"",company:"",email:"",phone:"",status:"Active",tags:[],notes:"",_prix:"",_prixOut:"",_prixAutre:"",_duree:"",_notesRest:""},
    deal:{title:"",contactId:"",stage:"Prospecting",value:0,probability:20,closeDate:"",notes:""},
    task:{title:"",contactId:"",priority:"Medium",dueDate:"",completed:false,notes:""},
  };
  const initForm = () => {
    if(!data) return defs[type];
    if(type==="contact") {
      const p = parseNotes(data.notes);
      return {...data, _prix:p.prix||"", _prixOut:p.prixOut||"", _prixAutre:p.prixAutre||"", _duree:p.duree||"", _notesRest:p.rest||""};
    }
    return {...data};
  };
  const [form,setForm] = useState(initForm());
  const set = (k,v) => setForm(f=>({...f,[k]:v}));
  const Title = type.charAt(0).toUpperCase()+type.slice(1);

  const handleSaveContact = () => {
    const notes = buildNotes(parseInt(form._prix)||0, parseInt(form._prixOut)||0, form._prixAutre||"", form._duree||"", form._notesRest||"");
    onSave({...form, notes});
  };

  return (
    <div className="ov" onClick={onClose}><div className="mo" onClick={e=>e.stopPropagation()}>
      <div className="mtl">{data?"Edit":"New"} {Title}</div>
      {type==="contact" && <>
        <div className="fr"><div className="ff"><label className="fl">Full Name *</label><input className="fi" value={form.fullName||""} onChange={e=>set("fullName",e.target.value)} /></div>
        <div className="ff"><label className="fl">Address *</label><input className="fi" value={form.company||""} onChange={e=>set("company",e.target.value)} /></div></div>
        <div className="fr"><div className="ff"><label className="fl">Email</label><input className="fi" type="email" value={form.email||""} onChange={e=>set("email",e.target.value)} /></div>
        <div className="ff"><label className="fl">Phone</label><input className="fi" value={form.phone||""} onChange={e=>set("phone",e.target.value)} /></div></div>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:11}}>
          <div className="ff"><label className="fl">Prix (In/Out) $</label><input className="fi" type="number" value={form._prix||""} onChange={e=>set("_prix",e.target.value)} placeholder="0" /></div>
          <div className="ff"><label className="fl">Prix OUT $</label><input className="fi" type="number" value={form._prixOut||""} onChange={e=>set("_prixOut",e.target.value)} placeholder="0" /></div>
          {data && (<div className="ff"><label className="fl">Status</label><select className="fi" value={form.status} onChange={e=>set("status",e.target.value)}><option>Active</option><option>Archived</option></select></div>)}
        </div>
        <div className="fr">
          <div className="ff"><label className="fl">Durée estimée {form._prix && !form._duree ? <span style={{color:"var(--t3)",fontWeight:400,textTransform:"none"}}>— auto: {autoEstimate(parseInt(form._prix)||0)}</span> : ""}</label><input className="fi" value={form._duree||""} onChange={e=>set("_duree",e.target.value)} placeholder={form._prix ? `Auto: ${autoEstimate(parseInt(form._prix)||0)}` : "Ex: 1h30, 2h, 45min"} /></div>
          <div className="ff"><label className="fl">Prix Autre / Détails</label><input className="fi" value={form._prixAutre||""} onChange={e=>set("_prixAutre",e.target.value)} placeholder="Ex: 250$ gouttières, 100$ garage..." /></div>
        </div>
        <div className="ff"><label className="fl">Notes</label><textarea className="fi" rows={3} value={form._notesRest||""} onChange={e=>set("_notesRest",e.target.value)} placeholder="Fréquence, détails, remarques..." /></div>
      </>}
      {type==="deal" && <>
        <div className="ff"><label className="fl">Deal Title *</label><input className="fi" value={form.title||""} onChange={e=>set("title",e.target.value)} /></div>
        <div className="fr"><div className="ff"><label className="fl">Contact</label><select className="fi" value={form.contactId||""} onChange={e=>set("contactId",e.target.value)}><option value="">— None —</option>{contacts.map(c=><option key={c.id} value={c.id}>{c.fullName} ({c.company})</option>)}</select></div>
        <div className="ff"><label className="fl">Stage</label><select className="fi" value={form.stage} onChange={e=>set("stage",e.target.value)}>{STAGES.map(s=><option key={s}>{s}</option>)}</select></div></div>
        <div className="fr"><div className="ff"><label className="fl">Value (CAD $)</label><input className="fi" type="number" value={form.value||0} onChange={e=>set("value",Number(e.target.value))} /></div>
        <div className="ff"><label className="fl">Probability (%)</label><input className="fi" type="number" min="0" max="100" value={form.probability||20} onChange={e=>set("probability",Number(e.target.value))} /></div></div>
        <div className="ff"><label className="fl">Close Date</label><input className="fi" type="date" value={form.closeDate||""} onChange={e=>set("closeDate",e.target.value)} /></div>
        <div className="ff"><label className="fl">Notes</label><textarea className="fi" rows={3} value={form.notes||""} onChange={e=>set("notes",e.target.value)} /></div>
      </>}
      {type==="task" && <>
        <div className="ff"><label className="fl">Task Title *</label><input className="fi" value={form.title||""} onChange={e=>set("title",e.target.value)} /></div>
        <div className="fr"><div className="ff"><label className="fl">Contact</label><select className="fi" value={form.contactId||""} onChange={e=>set("contactId",e.target.value)}><option value="">— None —</option>{contacts.map(c=><option key={c.id} value={c.id}>{c.fullName}</option>)}</select></div>
        <div className="ff"><label className="fl">Priority</label><select className="fi" value={form.priority} onChange={e=>set("priority",e.target.value)}>{PRIORITIES.map(p=><option key={p}>{p}</option>)}</select></div></div>
        <div className="ff"><label className="fl">Due Date</label><input className="fi" type="date" value={form.dueDate||""} onChange={e=>set("dueDate",e.target.value)} /></div>
        <div className="ff"><label className="fl">Notes</label><textarea className="fi" rows={2} value={form.notes||""} onChange={e=>set("notes",e.target.value)} /></div>
      </>}
      <div className="ma"><button className="btn bg" onClick={onClose}>Cancel</button><button className="btn bp" onClick={()=>type==="contact"?handleSaveContact():onSave(form)}>{data?"Save":"Add "+Title}</button></div>
    </div></div>
  );
}
